/* app/research-planner.jsx - Content Planner ("the kitchen"), wired to
   /api/content-plans (list/create/stage/delete) + /suggest + /inventory/propose,
   and to the PIECES of a plan (/content-plans/:planId/items).

   A plan is a body of work assembled from the banks, not one article. Its
   articles are its PIECES, so everything that belongs to one publishable post
   (title, page type, length, status, its own keyword, the post it became) lives
   on the piece, and "Generate draft" runs per piece and sends `plan_item_id`.
   The server rebuilds the whole brief from that id, which is what stopped the
   generator opening empty and asking the user to type in a keyword the plan
   already implied. */
import { api } from '../lib/api.js';
import { newRunId, pollRun } from '../lib/run.js';
(function () {
  const { useState, useEffect, useCallback, useRef } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const FD = (p) => React.createElement(window.MlBiHelpers.FacetDropdown, p);
  const arr = (v) => (Array.isArray(v) ? v : (v ? [v] : []));
  // prototype stage ↔ backend status (only "review" differs)
  const STAGE_IN = { in_review: "review" };
  const STAGE_OUT = { review: "in_review" };
  /* ── What is being written, which the chooser asks ─────────
     docs/CONTENT-TARGETS.md 7.1: `kind` is WHAT IS WRITTEN and where it goes,
     `purpose` is WHAT IT IS FOR. A row carries four columns for the answer and
     this screen writes two of them: `page_type`, the legacy label, and `kind`,
     which a request may now STATE. `purpose` follows a stated kind, and
     `kind_source` is refused by name on every path, so it is only ever read.

     The vocabulary lives in backend/lib/content-kind.js (KINDS, PURPOSES and
     the PAGE_TYPE_TARGET mapping) and no endpoint serves it, so this table is
     the ONE copy in the client and every label, badge, conditional field and
     request body below reads it rather than spelling a value again.

     THE TWO WAYS AN OPTION IS STORED, which is why `pageType` and `kind` are
     separate fields and not one:

       pageType set   the option IS a page_type, and the server derives kind and
                      purpose from it through the one mapping. Nothing is
                      stated, so the row records `derived`.
       pageType null  no page_type can express this kind (7.2 has no row that
                      maps to `help`), so the kind is STATED and the row records
                      `chosen`. `page_type` NEVER receives such a value: it
                      would fail that column's CHECK, and VALID_PAGE_TYPES below
                      is built from the pageType field precisely so this kind
                      cannot leak into it.

     `offered` is what somebody may choose now. The four that are not offered
     are why the list is written out rather than generated:

       blog      cannot say whether a blog post or the blog INDEX page was
                 meant. The server reads it as a post and records that reading
                 as a guess, so offering it would be asking for a guess.
       category  a taxonomy archive is generated by the CMS and nobody writes
       tag       one, so it is not a piece of a content plan at all.
       other     means nobody said, and choosing it would be saying so.

     A plan or a piece that already carries one of those four keeps it, is
     labelled for what it is, and can be moved off it. Nothing here rewrites a
     stored value on its own, and nothing offers one of them back. */
  const CONTENT_TARGETS = [
    { value: "post", pageType: "post", kind: "post", purpose: null, noun: "article", offered: true,
      label: "Blog post",
      sub: "A dated, authored article, filed under categories and tags." },
    { value: "service", pageType: "service", kind: "page", purpose: "service", noun: "page", offered: true,
      label: "Service page",
      sub: "Standing copy presenting an offering, built from the offer, the proof and the objections rather than from an angle and an outline." },
    { value: "landing", pageType: "landing", kind: "page", purpose: "landing", noun: "page", offered: true,
      label: "Landing page",
      sub: "Standing copy aimed at one next step, with one call to action." },
    { value: "help", pageType: null, kind: "help", purpose: null, noun: "help article", offered: true,
      label: "Help article",
      sub: "A support article on your own website, for people who already bought: the prerequisites, the numbered steps and what to do when a step goes wrong. Briefed in full, and where it would be published depends on what this site exposes." },
    { value: "blog", pageType: "blog", kind: "post", purpose: null, noun: "article", offered: false,
      label: "Blog post (stored as blog)",
      sub: "Stored as blog, which cannot say whether a post or the blog index page was meant, so it is read as a post." },
    { value: "category", pageType: "category", kind: null, purpose: null, noun: "piece", offered: false,
      label: "Category archive",
      sub: "A category archive is generated by the CMS, so nobody writes one and it is not a piece of a plan. Categories themselves are managed under Content." },
    { value: "tag", pageType: "tag", kind: null, purpose: null, noun: "piece", offered: false,
      label: "Tag archive",
      sub: "A tag archive is generated by the CMS, so nobody writes one and it is not a piece of a plan. Tags themselves are managed under Content." },
    { value: "other", pageType: "other", kind: null, purpose: null, noun: "piece", offered: false,
      label: "Not stated",
      sub: "Nobody said what this is, so nothing follows from it." },
  ];
  const OPTION_BY_VALUE = {}, OPTION_BY_PAGE_TYPE = {};
  for (const t of CONTENT_TARGETS) {
    OPTION_BY_VALUE[t.value] = t;
    if (t.pageType) OPTION_BY_PAGE_TYPE[t.pageType] = t;
  }
  /* Exactly the seven values the page_type CHECK allows, and it can never gain
     an eighth from this table: an option with no page_type contributes none.
     Used only where a raw page_type arrives from the server and has to be
     judged before it is sent back. */
  const VALID_PAGE_TYPES = new Set(CONTENT_TARGETS.filter(t => t.pageType).map(t => t.pageType));
  /* A stored value this table has never heard of is reported as unknown and
     never read as a post: an eighth page_type would be something nobody here
     has judged, which is not the same answer as "nobody said". */
  const UNKNOWN_TARGET = { value: null, pageType: null, kind: null, purpose: null, noun: "piece",
    offered: false, label: "Not stated",
    sub: "This is stored as something this screen does not recognise, so nothing can follow from it." };
  const optionOfValue = (v) => OPTION_BY_VALUE[String(v == null ? "" : v)] || UNKNOWN_TARGET;
  const optionOfPageType = (pt) => OPTION_BY_PAGE_TYPE[String(pt == null ? "" : pt)] || UNKNOWN_TARGET;
  /* A kind this screen can name, carrying a purpose it does not offer as an
     option of its own. Named rather than squeezed into the nearest option: a
     comparison page shown as "Service page" would be a wrong answer, where
     "Page" is a true one that happens to be less specific. */
  const KIND_ONLY = {
    post: { label: "Blog post", noun: "article" },
    page: { label: "Page", noun: "page" },
    help: { label: "Help article", noun: "help article" },
  };

  /* WHAT ONE STORED ROW IS, which needs both columns and one rule between them.
     A row carries a page_type AND a kind, and since a kind may be stated the
     two can legitimately disagree: a help article has no page_type at all, and
     a piece that inherited its kind from its plan carries the schema's default
     page_type beside a kind that did not come from it.

     THE RULE: when the kind agrees with what the page_type derives, the
     page_type's own option answers, which keeps the labels that are ABOUT a
     page_type (the ambiguous blog value, the two archives). When they disagree,
     the kind column answers, because a stated or inherited kind is the fact and
     the page_type beside it is a default standing in for silence. A row with no
     kind at all makes no claim, so its page_type answers alone. */
  function optionOfRow(row) {
    const pt = optionOfPageType(row && row.page_type);
    const kind = row && row.kind ? String(row.kind) : null;
    if (!kind || !KIND_ONLY[kind]) return pt;
    const purpose = row && row.purpose ? String(row.purpose) : null;
    if (pt.kind === kind && pt.purpose === purpose) return pt;
    const exact = CONTENT_TARGETS.find(t => t.kind === kind && t.purpose === purpose);
    if (exact) return exact;
    // A kind this screen can name carrying a purpose it does not offer, so
    // there is no option for it and one is made FROM THE ROW. `keep` is what
    // makes it safe to put in a chooser: selecting it sends nothing at all, so
    // the row keeps the answer it has, and the other options are still there to
    // move it off that answer deliberately.
    return { ...UNKNOWN_TARGET, value: "kept", keep: true, kind, purpose,
      label: KIND_ONLY[kind].label, noun: KIND_ONLY[kind].noun,
      sub: "Written for a purpose this screen does not offer as a choice of its own, so it is shown as what it is and left as it is unless you pick something else." };
  }
  /* What a chooser VALUE means, which needs the row whenever the row carries
     something this table does not offer: the kept option above exists only as
     a reading of that row, so it cannot be looked up in the list. */
  const chosenOption = (value, row) => {
    const of = optionOfValue(value);
    if (of !== UNKNOWN_TARGET) return of;
    const r = row ? optionOfRow(row) : UNKNOWN_TARGET;
    return r.value === value ? r : of;
  };
  /* The options a chooser shows: the four on offer, plus whatever this row
     already carries so an edit cannot silently rewrite it. */
  const targetOptions = (value) => CONTENT_TARGETS.filter(t => t.offered || t.value === value);

  /* WHERE the row's kind came from, which is the difference between a decision
     and a derivation and may not be guessed at.

     `kind_source` (backend/lib/content-kind.js) carries one of three values and
     this screen READS the answer and never computes one:

       chosen     somebody stated it for this row.
       inherited  it came from the plan, at the moment the piece was created.
       derived    it was read off the page type beside it.
       nothing    NO CLAIM, which is not the same as derived. A row written
                  before the column existed carries a null, and a screen that
                  called that "derived" would invent the one fact the column was
                  added to stop being invented.

     Nothing here SETS it: it is refused by name on every write path, because
     provenance follows from what a request sent rather than being claimed by
     it. Sending a kind makes it chosen, sending nothing lets a new piece
     inherit its plan's, and sending `kind: null` hands the row back to the
     mapping. */
  const SOURCE_SAID = {
    chosen: "Somebody chose this.",
    inherited: "Inherited from what the plan produces.",
    derived: "Read from the page type stored on this row.",
  };
  const targetSource = (row) => {
    const s = row && row.kind_source ? String(row.kind_source) : null;
    return SOURCE_SAID[s] ? s : null;
  };

  /* WHAT A SAVE SENDS for the answer in the chooser, which is not one field and
     not the same field every time.

     An option with a page_type sends the page_type and lets the server derive
     the pair. An option without one sends the kind, which is the only way to
     reach it and is recorded as chosen.

     AND THE WAY BACK, which is the half that is easy to miss: a CHOSEN or an
     INHERITED kind deliberately SURVIVES an edit of the page_type beside it,
     which is the whole point of the provenance column. So moving a row off such
     a kind takes `kind: null` in the same request, or the page_type would move
     while the old kind stayed and the row would say two things. A row whose kind
     was merely derived needs nothing: its page_type carries it.

     `row` is the row as it stands, or null when there is none yet. */
  function targetFields(value, row) {
    const t = chosenOption(value, row);
    // Leave this row's answer exactly as it is. Not a page_type, not a kind and
    // not a revert: the one request that says nothing about the target.
    if (t.keep) return {};
    if (!t.pageType) {
      // A stated kind. The page_type column is not mentioned at all: it cannot
      // hold this value, and the one the row already carries was never a claim
      // anybody made.
      // A kind of null would be the way BACK rather than a statement, so an
      // option with neither column to write says nothing instead.
      if (!t.kind) return {};
      return t.purpose ? { kind: t.kind, purpose: t.purpose } : { kind: t.kind };
    }
    const src = targetSource(row);
    return (src === "chosen" || src === "inherited")
      ? { page_type: t.pageType, kind: null }
      : { page_type: t.pageType };
  }
  const VALID_PRIORITIES = new Set(["none", "low", "medium", "high"]);
  const csv = (s) => s.split(",").map(x => x.trim()).filter(Boolean);
  // due_date comes back as a date or a full ISO timestamp depending on the
  // driver; <input type="date"> only accepts YYYY-MM-DD.
  const dateInput = (v) => { const s = v == null ? "" : String(v); return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : ""; };

  // The plan's progress, as the roll-up GET /content-plans returns per plan.
  // `known` separates "this server did not send a roll-up" from "this plan has
  // no pieces": both would otherwise render as "No pieces yet", and only one of
  // them is true about the plan.
  const rollup = (r) => ({
    known: !!r,
    pieces: Number(r && r.pieces) || 0,
    published: Number(r && r.published) || 0,
    drafted: Number(r && r.drafted) || 0,
    by_status: (r && r.by_status) || {},
  });
  const progressText = (it) => {
    if (!it.known) return "";
    return it.pieces ? `${it.published} of ${it.pieces} published` : "No pieces yet";
  };

  function mapPlan(p) {
    return {
      id: p.id, title: p.title || "(untitled)",
      prio: p.priority && p.priority !== "none" ? p.priority : "medium",
      stage: STAGE_IN[p.status] || p.status || "idea",
      cats: arr(p.categories).join(", "), tags: arr(p.tags).join(", "),
      target: p.target_url || "", due: p.due_date || "", audience: p.audience || "", tone: p.tone || "",
      words: p.word_count ? String(p.word_count) : "", brief: p.brief || "",
      // Legacy: a plan written before pieces existed carries its own keyword,
      // and briefForItem still falls back to it for a piece that has none. Shown
      // as what it is rather than as "the plan's keyword".
      legacyKw: p.primary_keyword || "",
      items: rollup(p.items),
      /* THE SECTION THAT WILL PUBLISH THIS PIECE, and the connection every
         write to this plan must be addressed to.

         `planSiteId` is the row's OWN site_id, not the switcher's, and that is
         the 8.6 lesson repeated: the To-do list sent the switcher's site_id on
         a delete and would have 404'd every row of another section, which on
         screen reads as "that row is gone". A plan of Blog (en) opened from the
         All list is read, patched, deleted and edited through Blog (en).

         `sectionName` and the verb are the server's, absent on the per
         connection read, which has one section and nothing to attribute. */
      planSiteId: p.site_id != null ? p.site_id : null,
      sectionId: p.section_id != null ? p.section_id : null,
      sectionName: p.section_name || "",
      canPublish: p.can_publish == null ? true : !!p.can_publish,
      verb: p.verb || "publish",
      verbWhy: p.verb_why || null,
      _raw: p,
    };
  }

  const STAGES = [["idea", "Idea"], ["briefed", "Briefed"], ["drafting", "Drafting"], ["review", "In review"], ["scheduled", "Scheduled"], ["published", "Published"], ["archived", "Archived"]];
  const STAGE_COLOR = { idea: "var(--gray-400)", briefed: "var(--blue-500)", drafting: "var(--indigo-600)", review: "var(--warning)", scheduled: "var(--info)", published: "var(--success)", archived: "var(--text-muted)" };

  // Piece statuses are the backend's own words, unmapped: this dropdown writes
  // straight to PATCH /items/:id, and a display alias would be a second
  // vocabulary to keep in step.
  const ITEM_STAGES = [["idea", "Idea"], ["briefed", "Briefed"], ["drafting", "Drafting"], ["in_review", "In review"], ["scheduled", "Scheduled"], ["published", "Published"], ["archived", "Archived"]];
  const ITEM_COLOR = { idea: "var(--gray-400)", briefed: "var(--blue-500)", drafting: "var(--indigo-600)", in_review: "var(--warning)", scheduled: "var(--info)", published: "var(--success)", archived: "var(--text-muted)" };

  // The banks a piece can claim material from, and how to read one row of each.
  //
  // `page` is deliberately not offered: briefForItem reads keyword, topic, faq
  // and gap edges only, so claiming a page would change nothing about the draft
  // while looking like it did. Page edges a plan already holds still render as
  // chips and are carried through untouched by every write below.
  const BANKS = [
    { kind: "keyword", label: "Keywords", path: "/keywords", name: (r) => r.keyword },
    { kind: "topic", label: "Topics", path: "/research/topics", name: (r) => r.title },
    { kind: "faq", label: "FAQs", path: "/research/faqs", name: (r) => r.question },
    { kind: "gap", label: "Gaps", path: "/todos", name: (r) => r.title },
  ];
  // Only keywords and topics derive an "in use" status from the edges; FAQs and
  // gaps have workflow vocabularies with no such member, so for those the answer
  // is unknown rather than "free".
  const DERIVES_USE = { keyword: true, topic: true };
  // The plan's unclaimed pool gets its own tab in the Material picker, ahead of
  // the banks. It is not a bank: those rows are already in the plan, so claiming
  // one adds nothing to the plan and only decides which piece is briefed on it.
  const POOL_TAB = "pool";
  const OFFERED_KINDS = new Set(BANKS.map(b => b.kind));
  const KIND_ICON = { keyword: "target", topic: "layers", page: "file", faq: "message-circle", gap: "flag" };
  const KIND_LABEL = { keyword: "Keyword", topic: "Topic", page: "Page", faq: "FAQ", gap: "Gap" };
  const KEY = (kind, refId) => `${kind}:${refId}`;

  // ── What a sweep of the banks could not use ───────────────
  // The reasons the server reports, in the order they are worth reading: the
  // first is the one the user can act on, and it is the one that covers most of
  // the bank on a site that has just imported keywords. A reason arriving from
  // a newer server with no wording here still renders, as its own name, rather
  // than vanishing and taking its count out of a total that stays the same.
  const UNUSABLE_ORDER = ["not_classified", "already_planned", "off_market", "off_topic", "archived"];
  const UNUSABLE_LABEL = {
    not_classified: "no category, product or cluster yet, so there is nothing to match them on",
    already_planned: "already held by a live plan",
    off_market: "judged off market",
    off_topic: "judged off topic",
    archived: "archived, so retired on purpose",
  };
  // The banks a sweep reads, in the server's order. FAQs and gaps carry no
  // facets, so nothing can be assembled around one and neither is swept.
  const SWEPT = [["topic", "topics"], ["keyword", "keywords"]];

  /** A timestamp as something a person can read, or "" when it is unreadable:
   *  an "Invalid Date" on screen is worse than saying nothing. */
  function whenText(v) {
    if (!v) return "";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
  }

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  /**
   * The reasons a sweep could not use something, as rows to render.
   *
   * Reads the per-bank breakdown the server sends and adds the two banks
   * together per reason, keeping the split so a row can say which bank it is
   * about. Anything the server counted under a reason this build has no wording
   * for is still listed, because dropping it would leave the rows shown adding
   * up to less than the total beside them.
   */
  function unusableRows(unusable) {
    if (!unusable) return [];
    const names = [...UNUSABLE_ORDER];
    for (const [kind] of SWEPT) {
      for (const r of Object.keys(unusable[kind] || {})) {
        if (r !== "total" && !names.includes(r)) names.push(r);
      }
    }
    return names.map(reason => {
      const per = SWEPT.map(([kind, label]) => [label, Number((unusable[kind] || {})[reason] || 0)]).filter(x => x[1] > 0);
      const count = per.reduce((a, x) => a + x[1], 0);
      return { reason, count, per, label: UNUSABLE_LABEL[reason] || reason };
    }).filter(r => r.count > 0);
  }

  /** Every ingredient a piece claims, flattened out of the per-kind groups. */
  function claimsOf(item) {
    const g = (item && item.ingredients) || {};
    const out = [];
    for (const kind of Object.keys(g)) {
      for (const r of arr(g[kind])) out.push({ kind, ref_id: Number(r.ref_id), ref_label: r.ref_label || "" });
    }
    return out;
  }

  /**
   * Why a plan's pool is empty, which is two different facts rather than one:
   * every piece has claimed its share, or the plan holds nothing at all. Only
   * one of those is a plan that is ready to be written, so neither may render as
   * blank space and leave the user to guess which one they are looking at.
   */
  function emptyPoolText(items) {
    const claimed = (items || []).reduce((n, it) => n + claimsOf(it).length, 0);
    return claimed
      ? `Nothing is waiting: all ${claimed} ingredient${claimed === 1 ? "" : "s"} this plan holds ${claimed === 1 ? "has" : "have"} been claimed by a piece.`
      : "This plan holds no material at all, claimed or unclaimed. Add some from the banks on any piece.";
  }

  /** "a", "a and b", "a, b and c": a list read as a sentence. */
  const andList = (parts) => {
    const p = (parts || []).filter(Boolean);
    if (p.length <= 1) return p[0] || "";
    return p.slice(0, -1).join(", ") + " and " + p[p.length - 1];
  };

  /** Ingredients as one flat list, from either the per-kind groups a piece
   *  carries or the flat array a write reports them in. */
  function flatClaims(v) {
    if (!v || typeof v !== "object") return [];
    const rows = Array.isArray(v)
      ? v.map(r => ({ kind: r && r.kind, ref: r }))
      : Object.keys(v).flatMap(kind => arr(v[kind]).map(r => ({ kind, ref: r })));
    return rows
      .map(({ kind, ref }) => ({ kind: String(kind || ""), ref_id: Number(ref && ref.ref_id), ref_label: (ref && ref.ref_label) || "" }))
      .filter(c => c.kind && Number.isFinite(c.ref_id));
  }

  /**
   * What a fill actually ATTACHED to a piece.
   *
   * The assemble route names it, but the ingredient list it returns is the
   * piece's whole list including the seed it started from, so reading that as
   * "what was added" would over-report on every call. Anything the reply does
   * not name explicitly is worked out by comparing what the piece claims now
   * with what it claimed before: the same fact from the other side, and one
   * that cannot credit the fill with material that was already there.
   */
  function addedBy(res, before) {
    const named = flatClaims(res && (res.added || res.added_ingredients));
    if (named.length) return named;
    const had = new Set(claimsOf(before).map(c => KEY(c.kind, c.ref_id)));
    const now = flatClaims((res && res.item && res.item.ingredients) || (res && res.ingredients));
    return now.filter(c => !had.has(KEY(c.kind, c.ref_id)));
  }

  /** "2 keywords and 1 topic", from a flat ingredient list. */
  function countText(claims) {
    const by = {};
    for (const c of claims) by[c.kind] = (by[c.kind] || 0) + 1;
    return andList(Object.keys(by).map(k => `${by[k]} ${(KIND_LABEL[k] || k).toLowerCase()}${by[k] === 1 ? "" : "s"}`));
  }

  /** The facets the server said it matched on, as words. Empty when it said
   *  nothing, so the report never invents a reason for the match. */
  const matchedText = (v) => andList(arr(v).map(x => String(x || "").trim()).filter(Boolean));

  /**
   * The tick list the Material picker renders, for rows already reduced to
   * { kind, id, label, sub }. Shared by the pool tab and the bank tabs so a tick
   * means exactly one thing wherever it is made, and so the row from the pool
   * carries its own kind rather than inheriting the tab's.
   *
   * A row can arrive `locked`: it is in the plan already, so it renders ticked
   * and refuses the click. Hiding it instead would make the bank look smaller
   * than it is, and offering it would promise a second add the server refuses.
   *
   * The tick is made by the checkbox's own onChange, NOT by a click handler on
   * the row. A <label> wrapping its checkbox forwards a click on the row to the
   * input, and that forwarded click bubbles back out through the label, so a
   * handler on the row fires TWICE for one real click: the tick landed and was
   * immediately undone, which made these rows all but impossible to select.
   * onChange fires once either way, whether the click lands on the box or on
   * the label's text, so the whole row stays clickable and ticks once.
   *
   * `head` is an optional row rendered above the list and inside the same
   * scroller, for the select-all control.
   */
  function Checklist({ visible, sel, toggle, head }) {
    return (
      <div className="bi-checklist">
        {head}
        {visible.map(r => {
          const k = KEY(r.kind, r.id);
          return (
            <label className="bi-checkrow" key={k} style={r.locked ? { opacity: 0.55, cursor: "default" } : undefined}>
              <span className="hds-check"><input type="checkbox" disabled={!!r.locked} checked={!!r.locked || !!sel[k]} onChange={() => { if (!r.locked) toggle(r.kind, r.id, r.label); }} /><span className="hds-check__box"><I name="check" size={12} /></span></span>
              <span className="bi-checkrow__name">{r.label}</span>
              <span className="bi-checkrow__sub">{r.sub}</span>
            </label>
          );
        })}
      </div>
    );
  }

  /* The chooser itself, one component so a plan and a piece ask the same
     question, in the same words, with the same answers. `value` is one of this
     table's option values, which is NOT always a page_type: an option whose
     kind no page_type can express has no page_type at all. */
  function TargetSelect({ value, onChange, label, current }) {
    const { Select } = window.HuddleDesignSystem_775113;
    const list = targetOptions(value);
    // A row whose answer is not one of this table's options still has to be
    // selectable, or the chooser would show somebody else's value as selected
    // and a save would quietly change what the row says.
    const shown = list.some(t => t.value === value) || !current ? list : [...list, current];
    return (
      <div>
        <label className="bi-field__label">{label}</label>
        <Select value={value} onChange={onChange}>
          {shown.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
      </div>
    );
  }

  /* ── WHERE A HELP ARTICLE WOULD BE PUBLISHED, per connection ──
     Read from GET /api/wp/publish-target rather than stated on the screen, for
     three reasons and the first one is the whole point of that endpoint: "this
     site has never been asked" and "this site exposes nothing a help article
     could go into" are opposite answers for a customer, only the connection
     knows which one is true, and a static sentence would have to pick one. The
     second is that it would become a lie the moment a site declares where its
     help articles live, since the screen would still be saying it cannot
     publish. The third is that the answer costs no network call: it is resolved
     from what the site already reported, which is why a screen may ask it while
     it renders.

     Read ONCE per site, with no `kind`, because the endpoint answers for all
     three kinds at once and that is what a chooser needs before it offers the
     choice.

     The endpoint's own `why` is NOT rendered. It is written for whoever has to
     fix the connection and names API routes, which is not language to put in
     front of a customer, so the STATE and the facts come from the endpoint and
     the sentence below is this screen's own. */
  const publicUrlOf = (site) => {
    const u = (site && (site.urlFull || site.url)) || "";
    return u.startsWith("http") ? u : (u ? "https://" + u : "");
  };

  function usePublishTargets(site) {
    const url = publicUrlOf(site);
    const [state, setState] = useState({ loading: !!url, targets: null, err: null, asked: !!url });
    useEffect(() => {
      if (!url) { setState({ loading: false, targets: null, err: null, asked: false }); return; }
      let live = true;
      setState({ loading: true, targets: null, err: null, asked: true });
      (async () => {
        try {
          const r = await api(`/wp/publish-target?siteUrl=${encodeURIComponent(url)}`);
          if (live) setState({ loading: false, targets: (r && r.targets) || null, err: null, asked: true });
        } catch (e) {
          // A failed READ of the answer is not an answer about the site, so it
          // says which of the two happened rather than reading as "cannot
          // publish".
          if (live) setState({ loading: false, targets: null, err: e.message || "the request failed", asked: true });
        }
      })();
      return () => { live = false; };
    }, [url]);
    return state;
  }

  /* The candidates a site exposes, named and counted from the live array. The
     count NEVER comes from a display cap: a list shortened for a sentence may
     only decide what to read first (feedback-truncated-lists-are-not-inventories). */
  const NAMED_CANDIDATES = 6;
  function candidateText(candidates) {
    const slugs = (candidates || []).map(c => c && c.slug).filter(Boolean);
    if (!slugs.length) return null;
    const shown = slugs.slice(0, NAMED_CANDIDATES);
    const rest = slugs.length - shown.length;
    return `${slugs.length} content type${slugs.length === 1 ? "" : "s"} a help article could go into (${shown.join(", ")}${rest ? `, and ${rest} more` : ""})`;
  }

  /* What this screen says about publishing a help article, in the words a
     customer uses, from the answer the connection gave. Every branch ends up
     saying the same two true things where they apply: the brief is real and can
     be handed over, and the publish is not. */
  function helpPublishText(pub) {
    if (!pub || !pub.asked) return "This site has no address stored here, so where a help article would be published was not looked up.";
    if (pub.loading) return "Reading where a help article would be published on this site.";
    if (pub.err) return `Where a help article would be published could not be read: ${pub.err}. Until that reads, treat it as a brief to hand over rather than something this app publishes.`;
    const t = pub.targets && pub.targets.help;
    if (!t) return "Where a help article would be published was not part of this site's answer, so treat it as a brief to hand over rather than something this app publishes.";
    const plan_and_brief = "You can plan one and generate its brief now; publishing it is what waits.";
    const named = candidateText(t.candidates);
    if (t.state === "resolved") {
      return t.declared
        ? `Help articles on this site go into its "${t.type}" content type, which somebody declared for this connection, so one can be published from here.`
        : `Help articles on this site go into its "${t.type}" content type, so one can be published from here.`;
    }
    if (t.reason === "never_asked") return `This site has not been asked yet which content types it has, so where a help article would be published is not known. ${plan_and_brief}`;
    if (t.reason === "read_failed") return `Reading this site's content types did not succeed, so where a help article would be published is not known. ${plan_and_brief}`;
    if (t.reason === "not_declared") {
      return named
        ? `Nothing has been named as where help articles live on this site. It exposes ${named}, and none of them has been picked: picking the wrong one would publish into the wrong place on a live site, so nothing guesses. ${plan_and_brief}`
        : `This site exposes no content type a help article could be published into, so one can be planned, briefed and handed over, but not published from here.`;
    }
    if (t.reason === "no_cms") return `This site is a public address with no CMS connected, so nothing is published from here at all. A help article can still be planned and briefed to hand to whoever owns the site.`;
    if (t.reason === "type_not_exposed") return `The content type help articles are meant to go into is not exposed by this site, so one cannot be published from here. ${plan_and_brief}`;
    // The two reasons below are about the ADDRESS a site serves a type at,
    // which is what lib/publish-target.js means by them as of 2026-10-19, and
    // not about whether the type has a title and an editor.
    if (t.reason === "type_not_writable") return `This site lists the content type help articles belong to and serves no address for it, so nothing can be published into it from here. ${plan_and_brief}`;
    if (t.reason === "writability_unknown") return `This site listed the content type help articles belong to without saying where it is served, so whether one can be published is not known. ${plan_and_brief}`;
    if (t.reason === "declaration_invalid") return `What was stored as the place help articles live on this site is not a content type, so nothing can be published until it is declared again. ${plan_and_brief}`;
    // A reason this screen has never heard of. Not read as a refusal and not as
    // a go ahead: an unjudged answer is its own state.
    return `Where a help article would be published on this site could not be established here, so treat it as a brief to hand over until somebody checks. ${plan_and_brief}`;
  }

  /* What follows from the answer, said where the answer is given, and NEVER as
     a figure: MarketLens measures no conversions and holds no ticket data, so
     what a page or a help article succeeds by is a sentence. An empty metric
     beside a page would read as a measured zero (7.4, and the same Number(null)
     reading that has now cost this codebase three bugs). */
  function TargetNote({ option, scope, publish }) {
    const t = option;
    const thing = scope === "plan" ? "Every piece of this plan" : "This piece";
    return (
      <>
        <p className="research-cost-note" style={{ marginTop: 10 }}>
          {t.sub}
          {t.kind === "post" && ` ${thing} is briefed as an article, and carries the categories and tags set here. Impressions, average position and the keyword it targets say whether it was found.`}
          {t.kind === "page" && ` ${thing} is briefed as a page, and is filed under no categories or tags. A WordPress page also carries a parent, a template and a menu position; MarketLens stores none of the three, so they are not asked for here. Search Console still says whether the page was found, and whatever it converts is measured nowhere in MarketLens, so nothing here reports that.`}
          {t.kind === "help" && ` ${thing} is briefed as a help article: prerequisites first, then numbered steps, then what to do when a step goes wrong, then where to go next. It carries no categories, tags or author here, and what a help article really succeeds by is deflecting a support ticket, which MarketLens holds no data about at all, so nothing here reports that.`}
          {t.kind === null && ` Nothing follows from it: the brief, the fields and the destination all wait on somebody saying what is written. Pick one of the four and this becomes answerable.`}
        </p>
        {/* Said at the moment of choosing, because the alternative is a customer
            finding out at publish time, after the writing is done. */}
        {t.kind === "help" && (
          <p className="research-cost-note">{helpPublishText(publish)}</p>
        )}
      </>
    );
  }

  function PlanForm({ onClose, site, onSaved, publish }) {
    const DS = window.HuddleDesignSystem_775113; const { Input, Select, Textarea } = DS;
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [title, setTitle] = useState("");
    // What this plan PRODUCES, and the default every piece of it starts as
    // (7.1). A plan that could not declare it would make a customer set every
    // piece one at a time, which is the thing the house rule about a new kind
    // belonging in the existing selector exists to prevent. The state is a
    // chooser VALUE, not a page_type: one of the options has no page_type.
    const [choice, setChoice] = useState("post");
    const [priority, setPriority] = useState("medium");
    const [due, setDue] = useState("");
    const [target, setTarget] = useState("");
    const [cats, setCats] = useState("");
    const [tags, setTags] = useState("");
    const [audience, setAudience] = useState("");
    const [tone, setTone] = useState("");
    const [words, setWords] = useState("1200");
    const [brief, setBrief] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    // What the fields below follow. Read from the one table, never spelled out
    // a second time here.
    const opt = optionOfValue(choice);
    const kind = opt.kind;
    const save = async () => {
      if (!title.trim()) { setErr("Working title is required."); return; }
      if (siteId == null) { setErr("No active site."); return; }
      setSaving(true); setErr(null);
      try {
        // No keyword or word count for one article here: those belong to a
        // piece, and a plan can hold several. The fields below are the defaults
        // every piece of this plan inherits, and what it PRODUCES is one of
        // them.
        //
        // Categories and tags are sent only for a plan that produces posts. A
        // page is filed under neither, so sending empty arrays for one would
        // store two facts nothing is true of, and the inputs are not shown
        // either: a field whose value has no meaning for what is being written
        // is worse than no field.
        await api(`/content-plans`, { method: "POST", body: JSON.stringify({
          site_id: siteId, title: title.trim(),
          // The chooser's answer, as whichever field can carry it. There is no
          // row yet, so there is nothing to hand back to the mapping.
          ...targetFields(choice, null),
          priority, due_date: due || null, target_url: target.trim() || null,
          ...(kind === "post" ? { categories: csv(cats), tags: csv(tags) } : {}),
          audience: audience.trim() || null,
          tone: tone.trim() || null, word_count: words ? parseInt(words, 10) : null,
          brief: brief.trim() || null, status: "idea", source: "manual",
        }) });
        onSaved && onSaved(); onClose();
      } catch (e) { setErr(e.message || "Save failed"); setSaving(false); }
    };
    return (
      <div className="bi-detail-section" style={{ marginBottom: 18 }}>
        <div className="bi-detail-section__h">New plan<button className="bi-iconbtn" onClick={onClose}><I name="x" size={16} /></button></div>
        <div className="bi-detail-section__body" style={{ paddingTop: 16 }}>
          <div className="bp-grid">
            <div className="bp-full"><label className="bi-field__label">Working title <span className="bi-field__req">*</span></label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. The 2026 guide to…" /></div>
            {/* First, because everything under it follows the answer. */}
            <TargetSelect label="What this plan produces" value={choice} current={opt} onChange={e => setChoice(e.target.value)} />
            <div><label className="bi-field__label">Priority</label><Select value={priority} onChange={e => setPriority(e.target.value)}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></Select></div>
            <div><label className="bi-field__label">Due date</label><Input type="date" value={due} onChange={e => setDue(e.target.value)} /></div>
            <div><label className="bi-field__label">Target URL</label><Input leadingIcon="link" value={target} onChange={e => setTarget(e.target.value)} placeholder="/section" /></div>
            {/* Categories and tags are a POST's fields (section 3). A page is
                filed under neither, so for a page they are not shown and not
                sent rather than shown and dropped. */}
            {kind === "post" && <div><label className="bi-field__label">Categories</label><Input value={cats} onChange={e => setCats(e.target.value)} placeholder="e.g. SEO" /></div>}
            {kind === "post" && <div><label className="bi-field__label">Tags</label><Input value={tags} onChange={e => setTags(e.target.value)} placeholder="comma separated" /></div>}
            <div><label className="bi-field__label">Audience</label><Input value={audience} onChange={e => setAudience(e.target.value)} placeholder="e.g. Founders" /></div>
            <div><label className="bi-field__label">Tone</label><Input value={tone} onChange={e => setTone(e.target.value)} placeholder="e.g. Practical" /></div>
            <div><label className="bi-field__label">Default word count</label><Select value={words} onChange={e => setWords(e.target.value)}><option value="800">800</option><option value="1200">1200</option><option value="1800">1800</option><option value="2500">2500</option></Select></div>
            <div className="bp-full"><label className="bi-field__label">Brief / notes</label><Textarea rows={3} value={brief} onChange={e => setBrief(e.target.value)} placeholder="Angle, audience, internal links, anything every piece of this plan needs." /></div>
          </div>
          <TargetNote option={opt} scope="plan" publish={publish} />
          <p className="research-cost-note" style={{ marginTop: 10 }}>These are the plan's defaults. Every piece inherits them, starts as what this plan produces, and can be set to something else or given its own length; the keyword and the draft belong to the piece.</p>
          {err && <div className="research-cost-note" style={{ color: "var(--danger)" }}>{err}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button className="bi-btn" onClick={onClose}>Cancel</button>
            <button className="bi-btn bi-btn--primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save plan"}</button>
          </div>
        </div>
      </div>
    );
  }

  // Edit an existing plan. Fields and values match PATCH /content-plans/:id:
  // the server quietly skips a priority or date it does not accept, which would
  // read as a save that did nothing, so validate here first.
  //
  // The article-shaped field (primary keyword) is gone: it is the piece's now,
  // and editing it here would let a plan disagree with the articles it is made
  // of. What the plan PRODUCES is not an article-shaped field, it is the plan's
  // own declaration and the default its pieces start from (7.1), so it is here.
  function EditPlanModal({ plan, siteId, onClose, onSaved, publish }) {
    const DS = window.HuddleDesignSystem_775113; const { Input, Select, Textarea } = DS;
    const raw = plan._raw || {};
    const [title, setTitle] = useState(raw.title || "");
    // Whatever this plan already carries, read from both columns by the one
    // rule: an edit may move a plan off an archive, off "not stated" or off a
    // kind nobody offers any more, and may not rewrite it on its own.
    const rowOpt = optionOfRow(raw);
    const [choice, setChoice] = useState(rowOpt.value || "post");
    const [priority, setPriority] = useState(VALID_PRIORITIES.has(raw.priority) ? raw.priority : "none");
    const [due, setDue] = useState(dateInput(raw.due_date));
    const [target, setTarget] = useState(raw.target_url || "");
    const [cats, setCats] = useState(arr(raw.categories).join(", "));
    const [tags, setTags] = useState(arr(raw.tags).join(", "));
    const [audience, setAudience] = useState(raw.audience || "");
    const [tone, setTone] = useState(raw.tone || "");
    const [words, setWords] = useState(raw.word_count ? String(raw.word_count) : "");
    const [brief, setBrief] = useState(raw.brief || "");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const opt = chosenOption(choice, raw);
    const kind = opt.kind;
    // What this plan still holds from when it was something else. Named rather
    // than counted, and never deleted here: the PATCH simply does not mention
    // the two columns, so a plan moved to a page keeps what it had if it is
    // ever moved back.
    const heldTaxonomy = kind === "post" ? [] : [...csv(cats), ...csv(tags)];
    const save = async () => {
      if (!title.trim()) { setErr("Working title is required."); return; }
      if (siteId == null) { setErr("No active site."); return; }
      if (!VALID_PRIORITIES.has(priority)) { setErr("Pick a priority from the list."); return; }
      if (words.trim() && !Number.isFinite(parseInt(words, 10))) { setErr("Word count must be a number."); return; }
      setSaving(true); setErr(null);
      try {
        // No ingredient-bearing field is sent, so the server leaves this plan's
        // edges alone rather than re-resolving them from names.
        await api(`/content-plans/${plan.id}?site_id=${siteId}`, { method: "PATCH", body: JSON.stringify({
          title: title.trim(),
          // Sent only when it actually changed, and then as whichever field can
          // carry it, with the way back where the row needs it. A save that
          // re-stamped the same answer would ask the server to resolve the three
          // target columns again on every unrelated edit of every plan, and
          // would turn an inherited or chosen answer into a derived one.
          ...(choice === rowOpt.value ? {} : targetFields(choice, raw)),
          priority, due_date: due || null,
          target_url: target.trim() || null,
          // Unmentioned rather than blanked for a plan that does not produce
          // posts: the fields are not on screen, so sending empty arrays would
          // be this screen deleting data it never showed anybody.
          ...(kind === "post" ? { categories: csv(cats), tags: csv(tags) } : {}),
          audience: audience.trim() || null, tone: tone.trim() || null,
          word_count: words.trim() ? parseInt(words, 10) : null, brief: brief.trim() || null,
        }) });
        if (onSaved) await onSaved();
        onClose();
      } catch (e) { setErr(e.message || "Save failed"); setSaving(false); }
    };
    return React.createElement(window.MlBiModals._Modal, {
      wide: true, title: "Edit plan", sub: raw.title || plan.title,
      onClose: saving ? () => {} : onClose,
      foot: (
        <>
          <button className="bi-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="bi-btn bi-btn--primary" onClick={save} disabled={saving || !title.trim()}>{saving ? "Saving…" : "Save changes"}</button>
        </>
      ),
    }, (
      <div className="bi-modal__body">
        <div className="bp-grid">
          <div className="bp-full"><label className="bi-field__label">Working title <span className="bi-field__req">*</span></label><Input value={title} onChange={e => setTitle(e.target.value)} /></div>
          <TargetSelect label="What this plan produces" value={choice} current={opt} onChange={e => setChoice(e.target.value)} />
          <div><label className="bi-field__label">Priority</label><Select value={priority} onChange={e => setPriority(e.target.value)}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option><option value="none">None</option></Select></div>
          <div><label className="bi-field__label">Due date</label><Input type="date" value={due} onChange={e => setDue(e.target.value)} /></div>
          <div><label className="bi-field__label">Target URL</label><Input leadingIcon="link" value={target} onChange={e => setTarget(e.target.value)} placeholder="/section" /></div>
          {/* A post's fields, and only a post's: section 3. */}
          {kind === "post" && <div><label className="bi-field__label">Categories</label><Input value={cats} onChange={e => setCats(e.target.value)} placeholder="comma separated" /></div>}
          {kind === "post" && <div><label className="bi-field__label">Tags</label><Input value={tags} onChange={e => setTags(e.target.value)} placeholder="comma separated" /></div>}
          <div><label className="bi-field__label">Audience</label><Input value={audience} onChange={e => setAudience(e.target.value)} placeholder="e.g. Founders" /></div>
          <div><label className="bi-field__label">Tone</label><Input value={tone} onChange={e => setTone(e.target.value)} placeholder="e.g. Practical" /></div>
          {/* free number, not a preset list: an existing plan can carry a count
              the presets would silently round away */}
          <div><label className="bi-field__label">Default word count</label><Input type="number" value={words} onChange={e => setWords(e.target.value)} placeholder="1200" /></div>
          <div className="bp-full"><label className="bi-field__label">Brief / notes</label><Textarea rows={3} value={brief} onChange={e => setBrief(e.target.value)} /></div>
        </div>
        <TargetNote option={opt} scope="plan" publish={publish} />
        {heldTaxonomy.length > 0 && (
          <p className="research-cost-note">This plan still holds {heldTaxonomy.join(", ")} from when it produced posts. Nothing files {opt.noun === "piece" ? "this" : `a ${opt.noun}`} under them, so they are left exactly as they are rather than deleted.</p>
        )}
        <p className="research-cost-note">Every piece of this plan inherits these. A piece can set its own word count, and its own keyword, status and what it is are edited on the piece.</p>
        {err && <div className="research-cost-note" style={{ color: "var(--danger)" }}>{err}</div>}
      </div>
    ));
  }

  // ── One piece: the article ────────────────────────────────
  // Add or edit. Everything here is a plan_items column, so a save is one PATCH
  // (or POST) and what comes back is what the row now holds.
  //
  // A NEW piece INHERITS what the plan produces (7.1), and the inheritance is a
  // default in this chooser rather than a value copied behind the user's back:
  // the piece is saved with whatever the chooser shows, so what the row ends up
  // carrying is what somebody saw. An existing piece keeps its own answer.
  function PieceModal({ planId, plan, piece, siteId, onClose, onSaved, publish }) {
    const DS = window.HuddleDesignSystem_775113; const { Input, Textarea } = DS;
    const editing = !!piece;
    const planRaw = (plan && plan._raw) || {};
    // The plan's own answer and this piece's own, each read from both columns
    // by the one rule. A new piece starts at the plan's.
    const planOpt = optionOfRow(planRaw);
    const rowOpt = editing ? optionOfRow(piece) : planOpt;
    const [title, setTitle] = useState((piece && piece.title) || "");
    const [choice, setChoice] = useState(rowOpt.value || "post");
    const [kw, setKw] = useState((piece && piece.primary_keyword) || "");
    const [words, setWords] = useState(piece && piece.word_count ? String(piece.word_count) : "");
    const [target, setTarget] = useState((piece && piece.target_url) || "");
    const [brief, setBrief] = useState((piece && piece.brief) || "");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const opt = chosenOption(choice, editing ? piece : planRaw);
    const kind = opt.kind;
    const noun = opt.noun;
    // Unchanged from what it started as, and therefore sendable as SILENCE:
    // an existing piece leaves all three of its target columns exactly as they
    // are, and a new piece lets the server record the answer as INHERITED from
    // the plan rather than as a choice this form made on somebody's behalf.
    //
    // For a NEW piece that is only safe when the plan's own kind was CHOSEN,
    // because that is the only case inheritance applies to
    // (lib/content-kind.js resolveTarget: a plan kind that was merely derived
    // is not inherited). A new piece of a derived plan therefore STATES its
    // page_type, or the chooser would show "Service page" and the row would
    // come back a post.
    const inherits = targetSource(planRaw) === "chosen";
    const kept = choice === rowOpt.value && (editing || inherits);
    const save = async () => {
      if (!title.trim()) { setErr("A piece needs a title."); return; }
      if (siteId == null) { setErr("No active site."); return; }
      if (words.trim() && !Number.isFinite(parseInt(words, 10))) { setErr("Word count must be a number."); return; }
      setSaving(true); setErr(null);
      const body = {
        site_id: siteId, title: title.trim(),
        ...(kept ? {} : targetFields(choice, editing ? piece : null)),
        primary_keyword: kw.trim() || null,
        word_count: words.trim() ? parseInt(words, 10) : null,
        target_url: target.trim() || null, brief: brief.trim() || null,
      };
      try {
        if (editing) await api(`/content-plans/${planId}/items/${piece.id}`, { method: "PATCH", body: JSON.stringify(body) });
        else await api(`/content-plans/${planId}/items`, { method: "POST", body: JSON.stringify(body) });
        if (onSaved) await onSaved();
        onClose();
      } catch (e) { setErr(e.message || "Save failed"); setSaving(false); }
    };
    return React.createElement(window.MlBiModals._Modal, {
      wide: true, title: editing ? "Edit piece" : "Add a piece",
      sub: editing ? (piece.title || "") : `One piece becomes one ${noun}.`,
      onClose: saving ? () => {} : onClose,
      foot: (
        <>
          <button className="bi-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="bi-btn bi-btn--primary" onClick={save} disabled={saving || !title.trim()}>{saving ? "Saving…" : editing ? "Save piece" : "Add piece"}</button>
        </>
      ),
    }, (
      <div className="bi-modal__body">
        <div className="bp-grid">
          <div className="bp-full"><label className="bi-field__label">Title <span className="bi-field__req">*</span></label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. How programmatic SEO actually scales" /></div>
          {/* First under the title, because what this piece IS decides what the
              rest of the form means. */}
          <TargetSelect label="What this piece is" value={choice} current={opt} onChange={e => setChoice(e.target.value)} />
          <div><label className="bi-field__label">Word count</label><Input type="number" value={words} onChange={e => setWords(e.target.value)} placeholder="inherits the plan's default" /></div>
          <div><label className="bi-field__label">Primary keyword</label><Input leadingIcon="search" value={kw} onChange={e => setKw(e.target.value)} placeholder="leave blank to use its strongest keyword" /></div>
          <div><label className="bi-field__label">Target URL</label><Input leadingIcon="link" value={target} onChange={e => setTarget(e.target.value)} placeholder={kind === "post" ? "/new-post" : kind ? `/where-this-${noun.replace(/ /g, "-")}-will-live` : "/where-this-will-live"} /></div>
          <div className="bp-full"><label className="bi-field__label">Notes for this piece</label><Textarea rows={3} value={brief} onChange={e => setBrief(e.target.value)} placeholder={`Anything specific to this ${noun}. The plan's brief is added to it.`} /></div>
        </div>
        <TargetNote option={opt} scope="piece" publish={publish} />
        {/* Where the default came from, said while it is still the default: an
            inherited value a user never saw would be indistinguishable from one
            they picked, and this one is also recorded differently. */}
        {!editing && kept && (
          <p className="research-cost-note">This is what the plan produces, and it is recorded as inherited from the plan rather than as a choice about this piece. Change it here and only this piece changes.</p>
        )}
        {/* A piece's categories, tags, author and excerpt are not asked for
            anywhere: plan_items has no column for any of the four, so the plan's
            own categories and tags are the only ones that exist. */}
        <p className="research-cost-note">Blank primary keyword means the generator writes against the strongest keyword this piece claims. Which one that is shows in the brief before you spend anything.</p>
        {err && <div className="research-cost-note" style={{ color: "var(--danger)" }}>{err}</div>}
      </div>
    ));
  }

  // ── Material: what this piece consumes ────────────────────
  // Ticking claims, unticking releases. A released ingredient is NOT deleted:
  // it goes back to the plan's pool, because the plan still consumes it.
  //
  // The plan's own unclaimed pool comes FIRST, on its own tab, because that is
  // the common case: material already in the plan that no piece has claimed
  // produces nothing at all until one does. The banks follow it, since adding
  // new material to a plan is legitimate too. A pool row is not repeated in its
  // bank tab, so every row on screen sits in exactly one place and "already in
  // this plan, unclaimed" is no longer indistinguishable from "free in the bank".
  //
  // The pool is read once, by the plan detail, and handed down. Two reads could
  // disagree about what is unclaimed while both were on screen.
  //
  // `piece` may be null, and that is PLAN mode: the same picker, but the save
  // ADDS to the plan's unclaimed pool instead of rewriting what one piece
  // claims. Only the keyword bank is offered there, because adding to the pool
  // is what the pool tab exists to receive, and a plan-level add of topics,
  // FAQs or gaps is a decision this screen has not been asked to make. Nothing
  // is pre-ticked, and a keyword the plan already holds, claimed or not, is
  // shown locked rather than offered twice: the server would refuse the second
  // add, and a tick that cannot land is a lie. `onReport` carries the result
  // sentence back to the detail view, where the pool it talks about is shown.
  function MaterialModal({ plan, piece, items, siteId, pool, poolErr, poolLoading, onRetryPool, onClose, onSaved, onReport }) {
    const DS = window.HuddleDesignSystem_775113; const { Input } = DS;
    const planMode = !piece;
    const [tab, setTab] = useState(planMode ? "keyword" : POOL_TAB);
    const [rows, setRows] = useState({});          // kind -> bank rows
    const [failed, setFailed] = useState([]);      // banks that would not load
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [sel, setSel] = useState(() => {
      const m = {};
      for (const c of claimsOf(piece)) m[KEY(c.kind, c.ref_id)] = c;
      return m;
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
      if (siteId == null) { setLoading(false); return; }
      let cancel = false;
      setLoading(true);
      // Plan mode reads one bank, not four: three reads whose rows could never
      // be shown would be spent purely on being thrown away.
      const banks = planMode ? BANKS.filter(b => b.kind === "keyword") : BANKS;
      Promise.all(banks.map(b =>
        api(`${b.path}?site_id=${siteId}`).then(r => ({ b, rows: Array.isArray(r) ? r : [] }), () => ({ b, rows: null }))))
        .then(res => {
          if (cancel) return;
          const next = {}, bad = [];
          for (const { b, rows: r } of res) {
            if (r === null) { bad.push(b.label); next[b.kind] = []; }
            else next[b.kind] = r;
          }
          setRows(next); setFailed(bad); setLoading(false);
        });
      return () => { cancel = true; };
    }, [siteId]);

    // Which pieces of THIS plan already hold each ingredient, so ticking one
    // that belongs to another piece says plainly that it is a move, not a copy.
    // In plan mode every piece's claims count, including none to skip, because
    // the question there is "is it in the plan at all", not "is it elsewhere".
    const owner = {};
    for (const it of (items || [])) {
      if (piece && Number(it.id) === Number(piece.id)) continue;
      for (const c of claimsOf(it)) owner[KEY(c.kind, c.ref_id)] = it.title || `Piece #${it.id}`;
    }

    const bank = BANKS.find(b => b.kind === tab) || BANKS[0];
    const inPool = tab === POOL_TAB;

    // What the plan holds unclaimed, in the kinds this picker can offer. Pages
    // are left out for the same reason they have no bank tab: a brief never
    // reads them, so claiming one would look like work and change nothing.
    const poolAll = arr(pool);
    const poolOffer = poolAll.filter(c => OFFERED_KINDS.has(c.kind)).map(c => ({
      kind: c.kind, id: c.ref_id, label: c.ref_label || `#${c.ref_id}`,
      sub: `${KIND_LABEL[c.kind] || c.kind} already in this plan, claimed by no piece`,
    }));
    const poolKeys = new Set(poolOffer.map(r => KEY(r.kind, r.id)));

    // The bank, minus whatever the pool tab is already showing. Rows another
    // piece claims stay here: they are not in the pool, and ticking one moves it.
    // The keyword list carries volume, intent and cluster, already paid for by
    // the GET, so plan mode shows them: they are the facts a keyword is picked on.
    const all = (rows[bank.kind] || []).map(r => ({ id: Number(r.id), label: String(bank.name(r) || "").trim(), status: r.status, volume: r.volume, intent: r.intent, cluster: r.cluster }))
      .filter(r => r.id && r.label);
    const bankOffer = all.filter(r => !poolKeys.has(KEY(bank.kind, r.id))).map(r => ({
      kind: bank.kind, id: r.id, label: r.label,
      sub: owner[KEY(bank.kind, r.id)]
        ? `claimed by "${owner[KEY(bank.kind, r.id)]}" in this plan, ticking it moves it here`
        : DERIVES_USE[bank.kind] ? (r.status === "in_use" ? "in use by a plan" : "free") : "",
    }));

    // Plan mode: the whole keyword bank, with what the plan already holds shown
    // locked in place rather than hidden. The two states of "already in" are
    // named apart, because one of them produces articles and the other does not.
    const kwFacts = (r) => {
      const bits = [];
      if (r.volume != null && r.volume !== "") bits.push(`vol ${r.volume}`);
      if (r.intent) bits.push(String(r.intent));
      if (r.cluster) bits.push(String(r.cluster));
      return bits.join(" · ");
    };
    const planOffer = all.map(r => {
      const k = KEY("keyword", r.id);
      const held = poolKeys.has(k) ? "" : owner[k];
      return (poolKeys.has(k) || held !== undefined)
        ? { kind: "keyword", id: r.id, label: r.label, locked: true, sub: held ? `already in this plan, claimed by "${held}"` : "already in this plan, unclaimed" }
        : { kind: "keyword", id: r.id, label: r.label, sub: kwFacts(r) };
    });

    const needle = q.trim().toLowerCase();
    const source = planMode ? planOffer : inPool ? poolOffer : bankOffer;
    const shown = needle ? source.filter(r => r.label.toLowerCase().includes(needle)) : source;
    const CAP = 200;
    const visible = shown.slice(0, CAP);

    // "The read failed", "there is nothing there" and "the filter matches
    // nothing" are three different facts, and each has to reach the user as
    // itself rather than as one shared shrug.
    const emptyText = planMode
      ? (needle ? "No keywords match that filter."
        : failed.includes("Keywords") ? "The keyword bank could not be read, so there is nothing to offer here. Close this and try again."
          : "No keywords in the bank for this site yet. Capture some in Keywords first.")
      : inPool
      ? (needle ? "Nothing unclaimed matches that filter."
        : poolAll.length ? "The only material this plan holds unclaimed is page references, and a brief never reads those, so they are not offered here."
          : emptyPoolText(items))
      : (needle ? "Nothing matches that filter."
        : all.length ? `Every row in ${bank.label} is already in this plan, on the first tab.`
          // A bank that would not load is not a bank with nothing in it, and
          // saying the second about the first is a claim about the user's data.
          : failed.includes(bank.label) ? `${bank.label} could not be read, so nothing from it is offered here.`
            : `Nothing in ${bank.label} for this site yet.`);

    const toggle = (kind, id, label) => setSel(s => {
      const k = KEY(kind, id), next = { ...s };
      if (next[k]) delete next[k]; else next[k] = { kind, ref_id: id, ref_label: label };
      return next;
    });

    // Select all, over EVERY row the filter matches rather than the first CAP
    // that happen to be rendered: searching "price" and ticking select-all has
    // to mean the 240 matches, not the 200 on screen. Locked rows are left out,
    // they are already in and cannot be ticked. A tick made under one filter
    // survives the next, because `sel` is keyed by row and not by what is shown.
    const pickable = shown.filter(r => !r.locked);
    const pickedAll = pickable.length > 0 && pickable.every(r => sel[KEY(r.kind, r.id)]);
    const toggleAll = () => setSel(s => {
      const next = { ...s };
      for (const r of pickable) {
        const k = KEY(r.kind, r.id);
        if (pickedAll) delete next[k];
        else next[k] = { kind: r.kind, ref_id: r.id, ref_label: r.label };
      }
      return next;
    });
    const allRow = pickable.length === 0 ? null : (
      <label className="bi-checkrow" style={{ borderBottom: "1px solid var(--border-subtle)", borderRadius: 0, position: "sticky", top: 0, background: "var(--bg-card)", zIndex: 1 }}>
        <span className="hds-check"><input type="checkbox" checked={pickedAll} onChange={toggleAll} /><span className="hds-check__box"><I name="check" size={12} /></span></span>
        <span className="bi-checkrow__name" style={{ fontWeight: 600 }}>
          {pickedAll
            ? `Clear all ${pickable.length}${needle ? " matching" : ""}`
            : `Select all ${pickable.length}${needle ? " matching" : ""}`}
        </span>
        <span className="bi-checkrow__sub">{Object.keys(sel).length ? `${Object.keys(sel).length} ticked` : ""}</span>
      </label>
    );

    const save = async () => {
      if (siteId == null) { setErr("No active site."); return; }
      setSaving(true); setErr(null);
      const ingredients = Object.values(sel).map(c => ({ kind: c.kind, ref_id: c.ref_id, ref_label: c.ref_label }));
      if (planMode) {
        // ADDITIVE, on purpose: the plan route attaches to the pool and leaves
        // every piece's claims alone, so nothing already assigned can be
        // disturbed from here. The server answers with what actually landed and
        // what it already held, and that split is reported rather than summed:
        // "5 added" when 2 were already there would claim work that did not happen.
        try {
          const res = await api(`/content-plans/${plan.id}/ingredients`, { method: "POST", body: JSON.stringify({ site_id: siteId, ingredients }) });
          const added = Number(res && res.added) || 0;
          const already = Number(res && res.already) || 0;
          if (onSaved) await onSaved();
          if (onReport) onReport({ tone: "info", text: `${added} added${already ? `, ${already} ${already === 1 ? "was" : "were"} already in the plan` : ""}.` });
          onClose();
        } catch (e) { setErr(e.message || "Could not add the keywords to this plan."); setSaving(false); }
        return;
      }
      try {
        // The full list, because the server replaces what this piece claims.
        // Anything dropped from it returns to the plan's pool.
        const res = await api(`/content-plans/${plan.id}/items/${piece.id}`, { method: "PATCH", body: JSON.stringify({ site_id: siteId, ingredients }) });
        // `unclaimed` is what the server could not use. Reporting it here is the
        // difference between a partial write and one that reads as complete.
        const skipped = (res && res.unclaimed) || [];
        if (skipped.length) { setErr(`${skipped.length} entr${skipped.length === 1 ? "y" : "ies"} could not be claimed and were left out.`); setSaving(false); return; }
        if (onSaved) await onSaved();
        onClose();
      } catch (e) { setErr(e.message || "Could not save what this piece claims."); setSaving(false); }
    };

    const counts = BANKS.map(b => [b.kind, Object.values(sel).filter(c => c.kind === b.kind).length]);
    const total = Object.keys(sel).length;

    return React.createElement(window.MlBiModals._Modal, {
      wide: true,
      title: planMode ? "Add keywords to this plan" : "Material for this piece",
      sub: planMode ? "They join the plan's unclaimed material. Pieces claim them from there." : (piece.title || ""),
      onClose: saving ? () => {} : onClose,
      foot: (
        <>
          <button className="bi-btn" onClick={onClose} disabled={saving}>Cancel</button>
          {/* Plan mode adds and nothing else, so zero ticked is a no-op the
              button refuses rather than a request the server answers with 0. */}
          {planMode
            ? <button className="bi-btn bi-btn--primary" onClick={save} disabled={saving || !total}>{saving ? "Adding…" : `Add ${total} keyword${total === 1 ? "" : "s"}`}</button>
            : <button className="bi-btn bi-btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : `Save (${total} claimed)`}</button>}
        </>
      ),
    }, (
      <div className="bi-modal__body">
        {/* Plan mode has one list, so tabs would be a row of one button: the
            keyword bank simply fills the modal. */}
        {!planMode && (
          <div className="bi-modal__tabs"><div className="hds-tabs">
            {/* The pool leads. Its badge counts what is WAITING there, not what is
                selected, and an unread pool shows no badge at all rather than a
                zero it never learned. */}
            <button className={"hds-tab " + (inPool ? "hds-tab--active" : "")} onClick={() => { setTab(POOL_TAB); setQ(""); }}>
              <I name="layers" size={15} />In this plan
              {pool && !poolErr && <span className="bi-conf">{poolOffer.length}</span>}
            </button>
            {BANKS.map(b => (
              <button key={b.kind} className={"hds-tab " + (tab === b.kind ? "hds-tab--active" : "")} onClick={() => { setTab(b.kind); setQ(""); }}>
                <I name={KIND_ICON[b.kind]} size={15} />{b.label}
                <span className="bi-conf">{(counts.find(c => c[0] === b.kind) || [])[1] || 0}</span>
              </button>
            ))}
          </div></div>
        )}
        {/* In plan mode a failed keyword read already owns the empty state
            below, so the strip would say the same thing twice. */}
        {!planMode && failed.length > 0 && (
          <div className="ds-strip ds-strip--error" style={{ marginBottom: 10 }}><I name="alert-triangle" size={14} /><span>{failed.join(" and ")} could not be read, so nothing from {failed.length === 1 ? "it" : "them"} is offered here. What you save still keeps everything this piece already claims.</span></div>
        )}
        {inPool && !poolLoading && !poolErr && poolOffer.length > 0 && (
          <p className="research-cost-note" style={{ marginTop: 10 }}>Already in this plan, claimed by no piece, so nothing is written from it yet. Ticking one claims it for this piece and adds nothing new to the plan.</p>
        )}
        <div style={{ margin: "10px 0" }}>
          <Input leadingIcon="search" value={q} onChange={e => setQ(e.target.value)} placeholder={planMode ? "Search the keyword bank" : inPool ? "Filter unclaimed material" : `Filter ${bank.label.toLowerCase()}`} />
        </div>
        {/* The pool has its own read, so it has its own loading and its own
            failure: a pool that could not be read must not read as a pool with
            nothing in it. */}
        {inPool
          ? (poolLoading
            ? <div className="research-progress-read"><span className="ml-spinner ml-spinner--sm" />Reading what this plan holds unclaimed…</div>
            : poolErr
              ? <div className="ds-strip ds-strip--error"><I name="alert-triangle" size={14} /><span>{poolErr}</span>{onRetryPool && <button className="bi-btn bi-btn--sm" onClick={onRetryPool}>Retry</button>}</div>
              : !pool
                ? <div className="research-cost-note">This plan's unclaimed material was not read, so none of it is offered here. The banks are on the tabs beside this one.</div>
                : visible.length === 0
                  ? <div className="research-cost-note">{emptyText}</div>
                  : <Checklist visible={visible} sel={sel} toggle={toggle} head={allRow} />)
          : (loading
            ? <div className="research-progress-read"><span className="ml-spinner ml-spinner--sm" />Reading the banks…</div>
            : visible.length === 0
              ? <div className="research-cost-note">{emptyText}</div>
              : <Checklist visible={visible} sel={sel} toggle={toggle} head={allRow} />)}
        {shown.length > CAP && <p className="research-cost-note">Showing the first {CAP} of {shown.length}. Filter to reach the rest.</p>}
        {/* The trailing notes explain the write the button makes, and the two
            modes make different writes, so they cannot share one explanation. */}
        {planMode
          ? <p className="research-cost-note">These keywords are added to the plan's unclaimed material, nothing is taken from any piece. A piece is briefed strictly on what it claims, so open Material on a piece to claim them before anything is written.</p>
          : (
            <>
              <p className="research-cost-note">Unticking gives an ingredient back to the plan's unclaimed pool, the first tab here, it is not deleted. Only keywords, topics, FAQs and gaps reach the brief, so pages are not offered here and any this plan already holds are left untouched.</p>
              <p className="research-cost-note">Claim as many keywords as belong together. An article is written against one primary and three secondaries, so the strongest three become the secondaries and anything past that stays claimed as spare material. The brief names which is which before you generate.</p>
            </>
          )}
        {err && <div className="research-cost-note" style={{ color: "var(--danger)" }}>{err}</div>}
      </div>
    ));
  }

  // ── Generate the draft for one piece ──────────────────────
  // Two steps, on purpose. The brief is read first (GET .../brief, free) so the
  // user sees what is actually behind the button, and a piece that is not ready
  // says why instead of opening an empty generator asking for a keyword. Then
  // POST /claude/generate carries `plan_item_id` and nothing else about the
  // brief: the server rebuilds it from the piece, so what was previewed is what
  // gets written.
  //
  // This is the ONE place that read still happens, and it happens for one piece
  // at the moment someone asks to spend something on it. The rows get their
  // can_generate off the pieces list instead: the same verdict, but the summary
  // shown here (which keyword, how many sections, how many FAQs) is more than a
  // list of pieces should carry, and it is only worth assembling for the piece
  // about to be written.
  function GeneratePieceModal({ plan, piece, siteId, onClose, onDrafted }) {
    const DS = window.HuddleDesignSystem_775113; const { Select } = DS;
    const [brief, setBrief] = useState(null);       // { ok, summary } | { ok:false, reason }
    const [readErr, setReadErr] = useState(null);   // the read itself failed
    const [reading, setReading] = useState(true);
    const [style, setStyle] = useState("informational");
    const [gen, setGen] = useState(false);
    const [run, setRun] = useState(null);
    const [err, setErr] = useState(null);
    const poll = useRef(null);
    useEffect(() => () => { if (poll.current) poll.current(); }, []);

    const read = useCallback(async () => {
      if (siteId == null) { setReading(false); setReadErr("No active site."); return; }
      setReading(true); setReadErr(null);
      try {
        const r = await api(`/content-plans/${plan.id}/items/${piece.id}/brief?site_id=${siteId}`);
        setBrief(r || null);
      } catch (e) {
        // A failed read is not "the piece is not ready": the route answers 200
        // with a reason for that. Keep them apart.
        setBrief(null); setReadErr(e.message || "Could not read this piece's brief.");
      } finally { setReading(false); }
    }, [plan.id, piece.id, siteId]);
    useEffect(() => { read(); }, [read]);

    const start = async () => {
      if (siteId == null) return;
      setGen(true); setErr(null); setRun(null);
      const runId = newRunId();
      poll.current = pollRun(runId, setRun);
      try {
        const r = await api(`/claude/generate`, { method: "POST", body: JSON.stringify({
          plan_item_id: piece.id, site_id: siteId, run_id: runId, style,
        }) });
        if (poll.current) { poll.current(); poll.current = null; }
        const article = (r && r.article) || null;
        if (!article) { setErr("The generator returned no article."); setGen(false); return; }
        // A draft that hit the length limit is still usable, so it is handed
        // over and the caller is told, rather than withheld or passed off as whole.
        onDrafted(article, (r && r.truncated && r.notice) || null, {
          plan_id: plan.id, item_id: piece.id, site_id: siteId, title: piece.title || "",
          status: (r && r.plan_item && r.plan_item.status) || null,
        });
      } catch (e) {
        if (poll.current) { poll.current(); poll.current = null; }
        setErr(e.message || "Generation failed."); setGen(false);
      }
    };

    const s = brief && brief.ok ? brief.summary || {} : null;
    const ready = !!s;
    const foot = gen
      ? <span className="bi-btn" aria-disabled="true" style={{ opacity: 0.6 }}>Generating…</span>
      : (
        <>
          <button className="bi-btn" onClick={onClose}>Cancel</button>
          <button className="bi-btn bi-btn--ai" disabled={!ready} onClick={start}><I name="sparkles" size={16} />Generate draft</button>
        </>
      );

    return React.createElement(window.MlBiModals._Modal, {
      title: "Generate this piece", sub: piece.title || "",
      onClose: gen ? () => {} : onClose, foot,
    }, (
      <div className="bi-modal__body">
        {gen ? (
          <div style={{ padding: "14px 0" }}>
            <div className="ml-progress-row" style={{ marginBottom: 10 }}>
              <div className="ml-progress"><div className="ml-progress__bar ml-progress__bar--ai" style={{ width: (run ? run.pct : 5) + "%" }} /></div>
              <span className="ml-progress-row__pct">{run ? run.pct : 5}%</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
              <span className="ml-spinner ml-spinner--sm ml-spinner--ai" />
              {run ? run.label : "Starting"}{run && run.note ? `: ${run.note}` : ""}
            </div>
          </div>
        ) : (
          <>
            {reading && <div className="research-progress-read"><span className="ml-spinner ml-spinner--sm" />Reading the brief…</div>}
            {!reading && readErr && (
              <div className="ds-strip ds-strip--error" style={{ marginBottom: 12 }}><I name="alert-triangle" size={14} /><span>{readErr}</span><button className="bi-btn bi-btn--sm" onClick={read}>Retry</button></div>
            )}
            {!reading && !readErr && brief && !brief.ok && (
              <div className="ds-strip ds-strip--quiet" style={{ marginBottom: 12 }}><I name="alert-triangle" size={14} /><span>{brief.reason || "This piece is not ready to generate yet."}</span></div>
            )}
            {!reading && ready && (
              <>
                <div className="bi-results-head"><I name="check-circle" size={16} style={{ color: "var(--success)" }} />What this piece will be written from</div>
                <div className="bi-detail-row"><span className="bi-detail-row__l">Primary keyword</span><span className="bi-detail-row__v">{s.primary_keyword}</span></div>
                {/* Named, not counted. A count answered "how many" when the
                    question a brief has to answer is "which", and with the
                    house rule capping this at three the difference between
                    claimed and targeted is a thing the user has to be able to
                    see before spending anything on the generation. */}
                <div className="bi-detail-row">
                  <span className="bi-detail-row__l">Secondary keywords</span>
                  <span className="bi-detail-row__v" style={{ fontWeight: 400 }}>
                    {(s.secondary_keywords || []).length ? s.secondary_keywords.join(", ") : "none"}
                    {s.keyword_rule ? ` (${s.secondary_count || 0} of ${s.keyword_rule.secondary} used)` : ""}
                  </span>
                </div>
                {Array.isArray(s.secondary_spare) && s.secondary_spare.length > 0 && (
                  <div className="ds-strip ds-strip--quiet" style={{ marginTop: 8, marginBottom: 8 }}>
                    <I name="info" size={14} />
                    <span>
                      This piece claims {s.secondary_spare.length} more {s.secondary_spare.length === 1 ? "keyword" : "keywords"} than an
                      article targets: {s.secondary_spare.join(", ")}. {s.secondary_spare.length === 1 ? "It stays" : "They stay"} claimed
                      by this piece as spare material. Move {s.secondary_spare.length === 1 ? "it" : "them"} to a piece of
                      {s.secondary_spare.length === 1 ? " its" : " their"} own from Material if {s.secondary_spare.length === 1 ? "it deserves" : "they deserve"} a page.
                    </span>
                  </div>
                )}
                <div className="bi-detail-row"><span className="bi-detail-row__l">Sections to cover</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{s.section_count || 0}</span></div>
                <div className="bi-detail-row"><span className="bi-detail-row__l">FAQs</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{s.faq_count || 0}</span></div>
                <div className="bi-detail-row"><span className="bi-detail-row__l">Gaps to close</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{s.gap_count || 0}</span></div>
                <div className="bi-detail-row"><span className="bi-detail-row__l">Length</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>~{s.words} words</span></div>
                <div style={{ marginTop: 14 }}>
                  <label className="bi-field__label">Style</label>
                  <Select value={style} onChange={e => setStyle(e.target.value)}>
                    <option value="informational">Informational</option>
                    <option value="how-to guide">How-to guide</option>
                    <option value="listicle">Listicle</option>
                    <option value="comparison">Comparison</option>
                  </Select>
                  <p className="research-cost-note">Style is the only choice left to make here. Everything else comes from the piece and its plan, assembled on the server.</p>
                </div>
              </>
            )}
            {err && <div className="ds-strip ds-strip--error" style={{ marginTop: 12 }}><I name="alert-triangle" size={14} /><span>{err}</span></div>}
          </>
        )}
      </div>
    ));
  }

  /**
   * Whether a piece can generate, read off the piece itself.
   *
   * GET /items now answers this for the whole plan: every piece carries
   * `can_generate` and a `reason` that is present either way, computed by the
   * same rule the generate modal's brief uses. It used to cost one GET
   * .../brief per piece on load and after every write, which a 22 piece plan
   * paid 22 times over for one fact per row.
   *
   * A piece with no boolean at all is "unknown", not "blocked": a server that
   * did not answer is not a server that said no, and the row has to be able to
   * say which of those happened.
   */
  function genState(item) {
    if (!item || typeof item.can_generate !== "boolean") return { state: "unknown" };
    if (item.can_generate) return { state: "ready" };
    return { state: "blocked", reason: item.reason || "This piece is not ready to generate yet." };
  }

  // ── One piece, in the plan ────────────────────────────────
  function PieceRow({ item, first, last, busy, fill, onStatus, onEdit, onMaterial, onGenerate, onOpenDraft, onDelete, onMove, onFill, onRecheck }) {
    const opt = optionOfRow(item);
    const src = targetSource(item);
    const claims = claimsOf(item);
    const can = genState(item);
    const kwCount = claims.filter(c => c.kind === "keyword").length;
    const linked = item.linked_post_id ? Number(item.linked_post_id) : null;
    return (
      <div className="research-row" style={{ paddingTop: 12, paddingBottom: 12, opacity: busy ? 0.6 : 1 }}>
        <div className="research-row__head">
          {/* What this piece is, in the words a customer uses. A kind somebody
              CHOSE says so on the line, because a decision and a derivation are
              not the same fact; a row that makes no claim about where its kind
              came from gets no badge rather than a guessed one. */}
          <span className="cp-pagetype" title={`${opt.label}. ${opt.sub}${src ? " " + SOURCE_SAID[src] : ""}`}>{opt.label}</span>
          {src === "chosen" && <span className="cluster-tag" title={SOURCE_SAID.chosen}>chosen</span>}
          <span className="research-row-title" style={{ fontWeight: 600 }}>{item.title}</span>
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <FD label="Status" value={item.status || "idea"} opts={ITEM_STAGES} colorOf={v => ITEM_COLOR[v] || "var(--text-secondary)"} down onChange={val => onStatus(item, val)} />
            <button className="bi-iconbtn" title="Move up" disabled={first || busy} onClick={() => onMove(item, -1)}><I name="chevron-up" size={16} /></button>
            <button className="bi-iconbtn" title="Move down" disabled={last || busy} onClick={() => onMove(item, 1)}><I name="chevron-down" size={16} /></button>
          </span>
        </div>
        <div className="bi-card__meta" style={{ marginTop: 8 }}>
          <div className="bi-meta-row"><span className="bi-meta-row__l">Keyword</span><span className="bi-meta-row__v">{item.primary_keyword ? item.primary_keyword : kwCount ? `strongest of ${kwCount} claimed` : "none yet"}</span></div>
          <div className="bi-meta-row"><span className="bi-meta-row__l">Length</span><span className="bi-meta-row__v">{item.word_count ? `${item.word_count} words` : "the plan's default"}</span></div>
          <div className="bi-meta-row"><span className="bi-meta-row__l">Draft</span><span className="bi-meta-row__v">{linked ? `post #${linked}` : "not linked yet"}</span></div>
        </div>
        {claims.length > 0
          ? <div className="ds-card__sample" style={{ marginTop: 8 }}>{claims.map(c => <span className="ds-chip" key={KEY(c.kind, c.ref_id)} title={`${KIND_LABEL[c.kind] || c.kind}: ${c.ref_label || c.ref_id}`}>{c.ref_label || `#${c.ref_id}`}</span>)}</div>
          : <p className="research-cost-note" style={{ marginTop: 8 }}>Nothing claimed yet. Add material and this piece can be written.</p>}
        {/* Said before the click rather than inside the generate modal, which
            is where the user finds out after deciding to spend something. */}
        {can.state === "blocked" && (
          <div className="ds-strip ds-strip--quiet" style={{ marginTop: 8 }}>
            <I name="alert-triangle" size={14} />
            <span>Cannot generate yet. {can.reason}</span>
            <button className="bi-btn bi-btn--sm" disabled={busy} onClick={() => onFill(item)}><I name="layers" size={14} />Fill from the banks</button>
          </div>
        )}
        {/* The pieces read, but this one came back without an answer, which is
            not the same as an answer of no. The row says what it does not know
            and offers the only thing that could change it: read them again. */}
        {can.state === "unknown" && (
          <div className="ds-strip ds-strip--error" style={{ marginTop: 8 }}>
            <I name="alert-triangle" size={14} />
            <span>The pieces list did not say whether this one can generate, so whether it has a keyword to write against is not known here.</span>
            <button className="bi-btn bi-btn--sm" disabled={busy} onClick={() => onRecheck(item)}>Retry</button>
          </div>
        )}
        {/* A refusal names classification as the thing that would make the fill
            work, and it is more specific than the brief's own reason, so it
            stays on the row rather than only in a note that gets dismissed. */}
        {fill && fill.ok === false && (
          <p className="research-cost-note" style={{ marginTop: 8 }}>Filling from the banks added nothing: {fill.reason}</p>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {onGenerate && <button className="bi-btn bi-btn--sm bi-btn--ai" disabled={busy} onClick={() => onGenerate(item)}><I name="sparkles" size={14} />Generate draft</button>}
          <button className="bi-btn bi-btn--sm" disabled={busy} onClick={() => onMaterial(item)}><I name="layers" size={14} />Material ({claims.length})</button>
          {linked && onOpenDraft && <button className="bi-btn bi-btn--sm" onClick={() => onOpenDraft(linked, item.title)}><I name="file-text" size={14} />Open draft</button>}
          <span style={{ flex: 1 }} />
          <button className="bi-iconbtn" title="Edit piece" disabled={busy} onClick={() => onEdit(item)}><I name="edit" size={16} /></button>
          <button className="bi-iconbtn" title="Delete piece" disabled={busy} onClick={() => onDelete(item)}><I name="trash" size={16} /></button>
        </div>
      </div>
    );
  }

  // ── The plan, as its pieces ───────────────────────────────
  function PlanDetail({ plan, siteId, onBack, onStage, onDelete, onEdit, onOpenDraft, onDraft, onChanged, publish }) {
    const DS = window.HuddleDesignSystem_775113; const { Input } = DS;
    // What this plan produces, which every piece of it starts as, read from
    // both columns by the one rule. A plan from before this question was asked
    // carries the create route's default, which is a post, and that is what it
    // is shown as.
    const planOpt = optionOfRow(plan._raw);
    const [items, setItems] = useState(null);     // null + err = failed read, [] = no pieces
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);       // id of the piece being written
    const [note, setNote] = useState(null);       // { tone, text }: what a write actually did
    const [editPiece, setEditPiece] = useState(null);   // { piece } or { piece: null } for a new one
    const [material, setMaterial] = useState(null);
    const [addKw, setAddKw] = useState(false);    // the plan-mode Material picker
    const [gen, setGen] = useState(null);
    const [confirm, setConfirm] = useState(null);
    // Proposed pieces: the AI grouping this plan's catalogue into articles.
    // Same shape as the planner-level propose, scoped to one plan's panel.
    const [pzOpen, setPzOpen] = useState(false);
    const [pzBusy, setPzBusy] = useState(false);
    const [pz, setPz] = useState([]);             // proposal cards, title editable in place
    const [pzErr, setPzErr] = useState(null);
    const [pzMeta, setPzMeta] = useState(null);   // { dropped, catalogue, note, truncated }
    const [pzRun, setPzRun] = useState(null);
    const [pzAccepting, setPzAccepting] = useState(null); // card key, or "all"
    const pzPoll = useRef(null);
    // Accepts since the last refresh. The list is re-read once, after the last
    // accept or when the panel closes, not after every card: five accepts are
    // one changed plan, not five reloads racing each other.
    const pzAccepted = useRef(0);
    useEffect(() => () => { if (pzPoll.current) pzPoll.current(); }, []);
    // The plan's unclaimed pool, read from GET /content-plans/:id/pool. null
    // means it was not read (either the read failed, and poolErr says so, or
    // there is no site), which is not the same as a pool with nothing in it.
    const [pool, setPool] = useState(null);       // flat [{ kind, ref_id, ref_label }]
    const [poolErr, setPoolErr] = useState(null);
    const [poolLoading, setPoolLoading] = useState(true);
    const [poolBusy, setPoolBusy] = useState(null);  // KEY of the chip being removed
    const [poolNote, setPoolNote] = useState(null);  // { tone, text }: what a pool write did, said beside the pool
    const [fills, setFills] = useState({});       // item id -> what the last fill answered for that piece
    const [fillRun, setFillRun] = useState(null); // { done, total } while walking the plan

    // The pieces, each already carrying whether it can generate and why not:
    // that is one request for the whole plan, not one per piece. Returns the
    // rows it read, or null when the read failed, so a caller that just filled
    // something reports what can NOW generate instead of what it hoped it did,
    // and can tell "still blocked" apart from "I could not find out".
    const load = useCallback(async () => {
      if (siteId == null) { setItems([]); setLoading(false); return []; }
      setLoading(true);
      let rows = null;
      try {
        const r = await api(`/content-plans/${plan.id}/items?site_id=${siteId}`);
        rows = Array.isArray(r) ? r : [];
        setItems(rows); setErr(null);
      } catch (e) {
        // An empty plan and a plan whose pieces would not load are different
        // things, so the failed read keeps `items` null and says so. There is
        // no per-piece answer at all in that case, which is why nothing below
        // reports a piece as blocked on the strength of a read that failed.
        setItems(null); setErr(e.message || "Could not load the pieces of this plan.");
      } finally { setLoading(false); }
      return rows;
    }, [plan.id, siteId]);
    useEffect(() => { load(); }, [load]);

    // What this plan holds that no piece has claimed. Read here rather than in
    // the Material picker so the section below and the picker cannot disagree
    // about what is waiting, and re-read after every piece write, because
    // claiming and releasing are exactly what change it.
    const loadPool = useCallback(async () => {
      if (siteId == null) { setPool(null); setPoolErr(null); setPoolLoading(false); return; }
      setPoolLoading(true);
      try {
        const r = await api(`/content-plans/${plan.id}/pool?site_id=${siteId}`);
        setPool(flatClaims(r && r.ingredients)); setPoolErr(null);
      } catch (e) {
        // A pool that could not be read must never render as an empty pool: one
        // says the plan is fully assigned, the other says nothing at all.
        setPool(null); setPoolErr(e.message || "Could not read what this plan holds unclaimed.");
      } finally { setPoolLoading(false); }
    }, [plan.id, siteId]);
    useEffect(() => { loadPool(); }, [loadPool]);

    // Every piece write changes the plan's roll-up, so the list behind this view
    // is refreshed with it rather than drifting until the next manual reload.
    // What comes back is every piece as it now stands, or null when the re-read
    // failed and nothing is known about any of them.
    const after = async () => { const rows = await load(); await loadPool(); if (onChanged) await onChanged(); return rows; };

    const setStatus = async (item, val) => {
      if (siteId == null) return;
      setBusy(item.id); setNote(null);
      try { await api(`/content-plans/${plan.id}/items/${item.id}`, { method: "PATCH", body: JSON.stringify({ site_id: siteId, status: val }) }); }
      catch (e) { setNote({ tone: "error", text: `Could not set the status of "${item.title}": ${e.message || "the request failed"}.` }); }
      finally { setBusy(null); await after(); }
    };

    // Reordering renumbers every piece from the order on screen. Swapping two
    // positions is not enough: legacy rows can share a position, and then the
    // list would not move at all while the write reported success.
    const move = async (item, dir) => {
      const list = items || [];
      const idx = list.findIndex(x => x.id === item.id);
      if (idx < 0 || idx + dir < 0 || idx + dir >= list.length) return;
      const next = [...list];
      next.splice(idx, 1); next.splice(idx + dir, 0, item);
      setBusy(item.id); setNote(null);
      let failed = 0;
      for (let i = 0; i < next.length; i++) {
        if (Number(next[i].position) === i) continue;
        try { await api(`/content-plans/${plan.id}/items/${next[i].id}`, { method: "PATCH", body: JSON.stringify({ site_id: siteId, position: i }) }); }
        catch { failed++; }
      }
      if (failed) setNote({ tone: "error", text: `${failed} piece${failed === 1 ? "" : "s"} could not be reordered. The order below is what the server now holds.` });
      setBusy(null); await after();
    };

    const removePiece = async (item) => {
      setConfirm(null);
      if (siteId == null) return;
      setBusy(item.id); setNote(null);
      try {
        const r = await api(`/content-plans/${plan.id}/items/${item.id}?site_id=${siteId}`, { method: "DELETE" });
        const freed = (r && r.released) || 0;
        if (freed) setNote({ tone: "info", text: `"${item.title}" is gone. ${freed} ingredient${freed === 1 ? "" : "s"} went back to the plan and can be claimed by another piece.` });
      } catch (e) { setNote({ tone: "error", text: `Could not delete "${item.title}": ${e.message || "the request failed"}.` }); }
      finally { setBusy(null); await after(); }
    };

    // Fill one piece from the banks. Deterministic and free: the server matches
    // FREE bank items on the facets this piece's own material already carries,
    // so it either attaches real rows or refuses and says why. There is no retry
    // and no looser second attempt, because material attached by coincidence is
    // material the user then has to unpick.
    const fillPiece = async (item) => {
      if (siteId == null) return;
      setBusy(item.id); setNote(null);
      let res = null, failed = null;
      try {
        res = await api(`/content-plans/${plan.id}/items/${item.id}/assemble`, { method: "POST", body: JSON.stringify({ site_id: siteId }) });
      } catch (e) { failed = e.message || "the request failed"; }
      setBusy(null);
      if (failed) {
        setNote({ tone: "error", text: `Could not fill "${item.title}": ${failed}. Nothing was changed.` });
        return;
      }
      if (res && res.ok === false) {
        // A refusal changes nothing, so there is nothing to reload: the reason
        // goes on the row, where it stays after the note is dismissed.
        const reason = res.reason || "the server did not say why.";
        setFills(f => ({ ...f, [item.id]: { ok: false, reason } }));
        setNote({ tone: "error", text: `Nothing was added to "${item.title}". ${reason}` });
        return;
      }
      setFills(f => ({ ...f, [item.id]: { ok: true } }));
      // The row now reads whatever the re-read list says, not what the fill
      // hoped: a piece that gained topics but still no keyword must still look
      // blocked.
      const rows = await after();
      const state = genState(rows && rows.find(r => Number(r.id) === Number(item.id)));
      const added = addedBy(res, item);
      const on = matchedText(res && res.matched_on);
      // Still blocked is the fact that matters, and the re-read list is the
      // authority on it. `has_keyword` is the server's own answer to the same
      // question, used only when the re-read could not tell us, so a warning is
      // never dropped just because the list read failed.
      const still = state.state === "blocked" ? ` It still cannot generate: ${state.reason}`
        : (state.state !== "ready" && res && res.has_keyword === false)
          ? ` ${res.note || "It still has no keyword, so it cannot generate a draft."}`
          : "";
      setNote({
        tone: added.length && !still ? "info" : "error",
        // A title folds the note to one line that opens a drawer (see the
        // render site). It carries the outcome's own numbers, nothing more.
        title: added.length
          ? `Filled "${item.title}": ${countText(added)}${still ? ", still cannot generate" : ""}`
          : `Nothing new attached to "${item.title}"`,
        text: added.length
          ? `Filled "${item.title}" with ${countText(added)}${on ? `, matched on ${on}` : ""}.${still}`
          : `Nothing new was attached to "${item.title}".${still}`,
        // Named, not just counted: a piece must never mysteriously acquire
        // material the user cannot see.
        chips: added,
      });
    };

    // Every piece that cannot generate, one request at a time.
    //
    // A plan promoted from a topic arrives with every piece bare, so this is the
    // common case rather than a convenience. It stops at the first request that
    // FAILS, because a route that failed once will fail for the rest and walking
    // on would turn one problem into a list of them. A piece the server REFUSES
    // is not a failure: that is an answer about that one piece, so it is
    // recorded on its row and the run carries on.
    const fillEveryBlocked = async () => {
      if (siteId == null || fillRun) return;
      const targets = (items || []).filter(it => genState(it).state === "blocked");
      if (!targets.length) return;
      setNote(null);
      setFillRun({ done: 0, total: targets.length });
      const answered = {};
      let filled = 0, stopped = null;
      for (let i = 0; i < targets.length; i++) {
        const it = targets[i];
        setFillRun({ done: i, total: targets.length });
        try {
          const res = await api(`/content-plans/${plan.id}/items/${it.id}/assemble`, { method: "POST", body: JSON.stringify({ site_id: siteId }) });
          if (res && res.ok === false) answered[it.id] = { ok: false, reason: res.reason || "the server did not say why." };
          else { filled++; answered[it.id] = { ok: true }; }
        } catch (e) { stopped = e.message || "the request failed"; break; }
      }
      setFills(f => ({ ...f, ...answered }));
      setFillRun(null);
      const rows = await after();
      const refused = Object.values(answered).filter(a => !a.ok).length;
      let text = `Filled ${filled} of ${targets.length}`;
      text += stopped ? `, then stopped: ${stopped}` : ".";
      if (refused) text += ` ${refused} had nothing to match on, and why is on the row.`;
      // The number the user actually came for, counted from the pieces just
      // re-read rather than from the fills that were attempted. A re-read that
      // failed knows nothing about any piece, so it says that instead of
      // counting zero of them as ready.
      let title = `Filled ${filled} of ${targets.length} piece${targets.length === 1 ? "" : "s"}`;
      if (stopped) title += ", then stopped";
      if (rows) {
        const ready = rows.filter(r => genState(r).state === "ready").length;
        text += ` ${ready} of ${rows.length} piece${rows.length === 1 ? "" : "s"} can now generate.`;
        title += `, ${ready} of ${rows.length} can now generate`;
      } else {
        text += " The pieces could not be re-read afterwards, so how many can now generate is not known here.";
      }
      // `title` folds the run's report to one line that opens a drawer (see
      // the render site); it is built from the same counts as the sentence.
      setNote({ tone: stopped || !filled || !rows ? "error" : "info", title, text });
    };

    // Take one keyword out of the plan's pool. The DELETE answers
    // { removed: 0, claimed: true } when a piece holds it, which can happen
    // whenever the pool on screen is older than the last claim, and that answer
    // has to reach the user as a sentence: a chip that quietly refuses to go
    // reads as the click not registering. Either way the pool is re-read,
    // because both answers mean what is on screen is no longer what is held.
    const removePoolIngredient = async (c) => {
      if (siteId == null || poolBusy) return;
      const k = KEY(c.kind, c.ref_id);
      const label = c.ref_label || `#${c.ref_id}`;
      setPoolBusy(k); setPoolNote(null);
      try {
        const r = await api(`/content-plans/${plan.id}/ingredients/${c.kind}/${c.ref_id}?site_id=${siteId}`, { method: "DELETE" });
        if (r && r.claimed) setPoolNote({ tone: "error", text: `"${label}" was not removed: a piece of this plan holds it now. Release it from that piece's Material first.` });
        else if (r && r.removed) setPoolNote({ tone: "info", text: `"${label}" was removed from this plan.` });
        else setPoolNote({ tone: "error", text: `"${label}" was not removed: the server reported nothing removed.` });
        await after();
      } catch (e) {
        setPoolNote({ tone: "error", text: `Could not remove "${label}": ${e.message || "the request failed"}.` });
      } finally { setPoolBusy(null); }
    };

    // ── Proposed pieces: the AI reads this plan's catalogue ──
    // Same contract shape as the planner-level propose: a run id first, so the
    // three server milestones are pollable while the request is in flight, and
    // the reply may only refer to rows it was given. A 400 is shown verbatim,
    // because the one the route sends ("no AI key") is already a user sentence.
    const proposePieces = async () => {
      if (siteId == null || pzBusy) return;
      setPzOpen(true); setPzBusy(true); setPz([]); setPzErr(null); setPzMeta(null); setPzRun(null);
      const runId = newRunId();
      pzPoll.current = pollRun(runId, setPzRun);
      try {
        const res = await api(`/content-plans/${plan.id}/propose-items`, { method: "POST", body: JSON.stringify({ site_id: siteId, count: 5, run_id: runId }) });
        // `_k` is the card's identity for the rest of its life: titles are
        // edited in place, so keying on the title would remount the input
        // under the user's cursor.
        setPz(((res && res.proposals) || []).map((p, i) => ({ ...p, _k: i })));
        const d = res && res.dropped;
        setPzMeta({
          // How many arrived, kept apart from how many are left on screen: an
          // emptied panel has to know whether it was emptied by accepting or
          // arrived empty, because only one of those is "nothing to group".
          proposed: ((res && res.proposals) || []).length,
          dropped: Array.isArray(d) ? d.length : Number(d) || 0,
          catalogue: (res && res.catalogue_size) || 0,
          note: (res && res.note) || null,
          truncated: !!(res && res.truncated),
        });
      } catch (e) {
        setPz([]);
        setPzErr(e.message || "Could not get proposals.");
      } finally {
        setPzBusy(false);
        if (pzPoll.current) { pzPoll.current(); pzPoll.current = null; }
      }
    };

    // Accepting writes the piece exactly as accepting a planner proposal does,
    // but into THIS plan, with the title as the user left it: the editable
    // field is the whole point of showing the card before writing anything.
    const acceptPiece = async (pr) => {
      if (siteId == null || pzAccepting != null) return;
      const title = String(pr.title || "").trim();
      if (!title) { setPzErr("A piece needs a title. Put one on the card before accepting it."); return; }
      setPzAccepting(pr._k); setPzErr(null);
      try {
        await api(`/content-plans/${plan.id}/items`, { method: "POST", body: JSON.stringify({
          site_id: siteId, title, page_type: pr.page_type,
          primary_keyword: pr.primary_keyword || null,
          ingredients: pr.ingredients,
        }) });
        pzAccepted.current += 1;
        const left = pz.filter(p => p._k !== pr._k);
        setPz(left);
        if (left.length === 0) { pzAccepted.current = 0; await after(); }
      } catch (e) {
        // The card stays: nothing was written, so a retry creates nothing twice.
        setPzErr(`Could not add "${title}": ${e.message || "the request failed"}`);
      } finally { setPzAccepting(null); }
    };

    // One at a time, like every other bulk walk in this file: in parallel a
    // failure would race the reload and the panel could not say which card did
    // not make it. Whatever fails stays on screen and is counted.
    const acceptAllPieces = async () => {
      if (siteId == null || pzAccepting != null || !pz.length) return;
      setPzAccepting("all"); setPzErr(null);
      const cards = [...pz];
      const kept = [];
      let firstMsg = "";
      for (const pr of cards) {
        const title = String(pr.title || "").trim();
        if (!title) { kept.push(pr); firstMsg = firstMsg || "a piece needs a title"; continue; }
        try {
          await api(`/content-plans/${plan.id}/items`, { method: "POST", body: JSON.stringify({
            site_id: siteId, title, page_type: pr.page_type,
            primary_keyword: pr.primary_keyword || null,
            ingredients: pr.ingredients,
          }) });
          pzAccepted.current += 1;
        } catch (e) { kept.push(pr); firstMsg = firstMsg || e.message || "the request failed"; }
      }
      setPz(kept);
      if (kept.length) setPzErr(`${kept.length} of ${cards.length} could not be added: ${firstMsg}`);
      setPzAccepting(null);
      if (pzAccepted.current) { pzAccepted.current = 0; await after(); }
    };

    const closePropose = async () => {
      setPzOpen(false); setPzErr(null); setPzRun(null);
      if (pzPoll.current) { pzPoll.current(); pzPoll.current = null; }
      // Accepts the panel still owed a refresh for are settled on the way out.
      if (pzAccepted.current) { pzAccepted.current = 0; await after(); }
    };

    const list = items || [];
    // Only pieces the list actually said no about. One that came back without
    // an answer is counted separately, because offering to fill it would be
    // acting on something nobody said.
    const blocked = list.filter(it => genState(it).state === "blocked");
    const unknown = list.filter(it => genState(it).state === "unknown");
    const done = list.filter(i => i.status === "published").length;
    const drafted = list.filter(i => i.linked_post_id).length;
    const pct = list.length ? Math.round((done / list.length) * 100) : 0;

    return (
      <div className="bi-detail" style={{ maxWidth: 900 }}>
        <button className="bi-detail__back" onClick={onBack}><I name="chevron-left" size={16} />Back to Content Planner</button>
        <div className="bi-detail-head">
          <div>
            <div className="bi-detail__name">{plan.title}</div>
            <div className="bi-detail__badges">
              <span className={"priority-badge priority-" + plan.prio}>{plan.prio}</span>
              <FD label="Plan status" value={plan.stage} opts={STAGES} colorOf={v => STAGE_COLOR[v]} down onChange={val => onStage && onStage(plan.id, val)} />
            </div>
          </div>
          <div className="bi-detail-actions">
            <button className="bi-btn bi-btn--primary" onClick={() => setEditPiece({ piece: null })}><I name="plus" size={16} />Add piece</button>
            {/* Plan-level add: keywords land in the unclaimed pool below, not on
                any piece, so this is stocking the plan rather than briefing it. */}
            <button className="bi-btn" onClick={() => setAddKw(true)}><I name="target" size={16} />Add keywords</button>
            <button className="bi-btn bi-btn--ai" disabled={pzBusy} onClick={proposePieces}><I name="shuffle" size={16} />{pzBusy ? "Proposing..." : "Propose pieces"}</button>
            <button className="bi-btn" onClick={() => onEdit && onEdit(plan)}><I name="edit" size={16} />Edit plan</button>
            <button className="bi-btn bi-btn--danger" onClick={() => setConfirm({ kind: "plan" })}><I name="trash" size={16} />Delete plan</button>
          </div>
        </div>

        {/* Progress, counted from the pieces actually on screen. */}
        <div className="bi-detail-section">
          <div className="bi-detail-section__h">Progress</div>
          <div className="bi-detail-section__body" style={{ paddingTop: 16, paddingBottom: 18 }}>
            {err
              ? <div className="ds-strip ds-strip--error"><I name="alert-triangle" size={14} /><span>{err}</span><button className="bi-btn bi-btn--sm" onClick={load}>Retry</button></div>
              : loading
                ? <div className="research-progress-read"><span className="ml-spinner ml-spinner--sm" />Reading the pieces…</div>
                : (
                  <>
                    <div className="ml-progress-row" style={{ marginBottom: 10 }}>
                      <div className="ml-progress"><div className="ml-progress__bar" style={{ width: pct + "%" }} /></div>
                      <span className="ml-progress-row__pct">{pct}%</span>
                    </div>
                    <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
                      {list.length ? `${done} of ${list.length} published · ${drafted} with a draft attached` : "No pieces yet. A plan yields several articles; add the first one."}
                    </div>
                  </>
                )}
          </div>
        </div>

        {/* The pieces */}
        <div className="bi-detail-section">
          <div className="bi-detail-section__h">Pieces{!err && !loading && <span className="bi-conf">{list.length}</span>}</div>
          <div className="bi-detail-section__body">
            {/* A note WITH a title is a run report (a fill's outcome): one
                line that opens a drawer holding the full sentence and the
                named material, so it stops sitting on top of the pieces it
                reports on. A note without one is a single sentence and stays
                inline, because a drawer around one line would be ceremony.
                Dismiss means the same thing on both: clear the note. */}
            {note && (note.title
              ? (
                <window.MlReportDrawer.ReportStrip title={note.title} tone={note.tone === "error" ? "problem" : "opportunity"} onDismiss={() => setNote(null)}>
                  <p className="ds-card__detail">{note.text}</p>
                  {arr(note.chips).length > 0 && (
                    <div className="ds-card__sample" style={{ marginTop: 6 }}>
                      {note.chips.map(c => <span className="ds-chip" key={KEY(c.kind, c.ref_id)} title={`${KIND_LABEL[c.kind] || c.kind}: ${c.ref_label || c.ref_id}`}>{c.ref_label || `#${c.ref_id}`}</span>)}
                    </div>
                  )}
                </window.MlReportDrawer.ReportStrip>
              )
              : (
                <div className="research-cost-note" style={{ color: note.tone === "error" ? "var(--danger)" : undefined }}>
                  {note.text} <button className="bi-btn bi-btn--sm" onClick={() => setNote(null)}>Dismiss</button>
                  {arr(note.chips).length > 0 && (
                    <div className="ds-card__sample" style={{ marginTop: 6 }}>
                      {note.chips.map(c => <span className="ds-chip" key={KEY(c.kind, c.ref_id)} title={`${KIND_LABEL[c.kind] || c.kind}: ${c.ref_label || c.ref_id}`}>{c.ref_label || `#${c.ref_id}`}</span>)}
                    </div>
                  )}
                </div>
              ))}
            {/* Pieces the list returned with no verdict. Any count of what
                cannot generate is drawn from the pieces that answered, so what
                is missing from it is said here rather than left to read as a
                figure for the whole plan. */}
            {!err && !loading && unknown.length > 0 && (
              <div className="ds-strip ds-strip--error">
                <I name="alert-triangle" size={14} />
                <span>{unknown.length} of {list.length} piece{unknown.length === 1 ? "" : "s"} came back without saying whether {unknown.length === 1 ? "it" : "they"} can generate, so nothing here counts {unknown.length === 1 ? "it" : "them"} either way.</span>
                <button className="bi-btn bi-btn--sm" disabled={!!busy || !!fillRun} onClick={load}>Retry</button>
              </div>
            )}
            {/* The plan-level version of the same fix, for a plan whose pieces
                were all promoted bare. It offers itself only when there is
                something it would actually act on. */}
            {!err && !loading && blocked.length > 0 && (
              <div className="bi-bulk-bar">
                {fillRun ? (
                  <span className="research-progress-read"><span className="ml-spinner ml-spinner--sm" />Filling piece {Math.min(fillRun.done + 1, fillRun.total)} of {fillRun.total}…</span>
                ) : (
                  <>
                    <span className="bi-bulk-bar__count">{blocked.length} of {list.length} cannot generate yet</span>
                    <span className="bi-bulk-bar__sep" />
                    <span style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>Filling matches free bank items on the facets a piece already carries. No AI, nothing spent.</span>
                    <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={!!busy} onClick={fillEveryBlocked}><I name="layers" size={14} />Fill every piece that cannot generate ({blocked.length})</button>
                  </>
                )}
              </div>
            )}
            {/* Proposed pieces, right above the list they would join. Cards are
                writable before they are written: the title lands as edited. */}
            {pzOpen && (
              <div className="cp-suggest-panel">
                <div className="cp-suggest-panel__head">
                  <div className="cp-suggest-panel__title"><I name="shuffle" size={18} />Proposed pieces</div>
                  {!pzBusy && (
                    <div style={{ display: "flex", gap: 8 }}>
                      {pz.length > 1 && <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={pzAccepting != null} onClick={acceptAllPieces}>{pzAccepting === "all" ? "Adding..." : `Accept all (${pz.length})`}</button>}
                      <button className="bi-btn bi-btn--sm" disabled={pzAccepting != null} onClick={closePropose}>Dismiss</button>
                    </div>
                  )}
                </div>
                {/* The 400 for a missing AI key arrives here word for word: the
                    route writes it for the user, so rewording it would only
                    bury which key it means. */}
                {pzErr && <div className="ds-strip ds-strip--error" style={{ marginBottom: 12 }}><I name="alert-triangle" size={14} /><span>{pzErr}</span><button className="bi-btn bi-btn--sm" onClick={() => setPzErr(null)}>Dismiss</button></div>}
                {pzBusy
                  ? (
                    /* Milestones the server actually reached, not a timer. */
                    <div style={{ padding: "12px 0" }}>
                      <div className="ml-progress-row" style={{ marginBottom: 10 }}>
                        <div className="ml-progress"><div className="ml-progress__bar ml-progress__bar--ai" style={{ width: (pzRun ? pzRun.pct : 5) + "%" }} /></div>
                        <span className="ml-progress-row__pct">{pzRun ? pzRun.pct : 5}%</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
                        <span className="ml-spinner ml-spinner--sm ml-spinner--ai" />
                        {pzRun ? pzRun.label : "Starting"}{pzRun && pzRun.note ? `: ${pzRun.note}` : ""}
                      </div>
                    </div>
                  )
                  : pz.length === 0
                    ? (pzErr ? null : (
                      <div className="research-cost-note" style={{ padding: "12px 0" }}>
                        {pzMeta && pzMeta.proposed > 0
                          ? "Every proposal was accepted. The pieces are in the list below."
                          : (pzMeta && pzMeta.note) || "The catalogue had nothing free to group into a piece for this plan."}
                      </div>
                    ))
                    : (
                      <>
                        {/* What the model was given and what was thrown away, so
                            a short list is explained rather than looking thin. */}
                        {pzMeta && (
                          <p className="research-cost-note" style={{ marginBottom: 12 }}>
                            Grouped from {pzMeta.catalogue} free item{pzMeta.catalogue === 1 ? "" : "s"}.
                            {pzMeta.dropped ? ` ${pzMeta.dropped} reference${pzMeta.dropped === 1 ? " was" : "s were"} discarded for pointing at nothing in your banks.` : ""}
                            {pzMeta.truncated ? " The reply was cut short, so there may be fewer here than asked for." : ""}
                            {" "}Edit a title before accepting and the piece is created with your words.
                          </p>
                        )}
                        <div className="cp-suggest-grid">{pz.map(pr => (
                          <div className="bi-card" key={pr._k} style={{ cursor: "default" }}>
                            <Input value={pr.title || ""} onChange={e => { const v = e.target.value; setPz(cards => cards.map(p => p._k === pr._k ? { ...p, title: v } : p)); }} />
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                              <span className="cp-pagetype" title={optionOfPageType(pr.page_type).sub}>{optionOfPageType(pr.page_type).label}</span>
                              {pr.primary_keyword && <span className="research-tag research-tag--intent" style={{ textTransform: "none" }}>{pr.primary_keyword}</span>}
                            </div>
                            {pr.why && <p className="research-row-desc" style={{ marginTop: 4, fontStyle: "italic" }}>{pr.why}</p>}
                            {pr.brief && <p className="research-row-desc" style={{ marginTop: 4 }}>{pr.brief}</p>}
                            {/* Named, not counted: accepting claims these exact
                                rows for the new piece. */}
                            <div className="ds-card__sample">
                              {arr(pr.ingredients).map(ing => (
                                <span className="ds-chip" key={KEY(ing.kind, ing.ref_id)} title={`${KIND_LABEL[ing.kind] || ing.kind}: ${ing.ref_label || ing.ref_id}`}>{ing.ref_label || `#${ing.ref_id}`}</span>
                              ))}
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
                              <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={pzAccepting != null || !String(pr.title || "").trim()} onClick={() => acceptPiece(pr)}>
                                <I name="plus" size={14} />{pzAccepting === pr._k ? "Adding..." : `Accept with ${plural(arr(pr.ingredients).length, "item", "items")}`}
                              </button>
                            </div>
                          </div>
                        ))}</div>
                      </>
                    )}
              </div>
            )}
            {err
              ? <div className="research-cost-note">The pieces could not be read, so none are listed. Retry above.</div>
              : loading
                ? <div className="research-progress-read"><span className="ml-spinner ml-spinner--sm" />Reading the pieces…</div>
                : list.length === 0
                  ? (
                    <div className="bi-empty-state">
                      <div className="bi-empty-state__title">This plan has no pieces</div>
                      <div className="bi-empty-state__desc">A plan is a body of work: each piece becomes one published post, with its own keyword, length and draft.</div>
                      <div className="bi-empty-state__actions"><button className="bi-btn bi-btn--primary" onClick={() => setEditPiece({ piece: null })}><I name="plus" size={16} />Add the first piece</button></div>
                    </div>
                  )
                  : (
                    <div className="research-rows">
                      {list.map((it, i) => (
                        <PieceRow key={it.id} item={it} first={i === 0} last={i === list.length - 1}
                          busy={busy === it.id || !!fillRun}
                          fill={fills[it.id]}
                          onFill={fillPiece} onRecheck={load}
                          onStatus={setStatus}
                          onEdit={(x) => setEditPiece({ piece: x })}
                          onMaterial={(x) => setMaterial(x)}
                          onGenerate={onDraft ? ((x) => setGen(x)) : null}
                          onOpenDraft={onOpenDraft}
                          onDelete={(x) => setConfirm({ kind: "piece", item: x })}
                          onMove={move} />
                      ))}
                    </div>
                  )}
            <p className="research-cost-note">
              Material a piece releases goes back to the plan and stays there until another piece claims it. What is waiting is listed below, and "Material" offers it before the banks.
            </p>
          </div>
        </div>

        {/* The plan's own material that no piece has claimed: the plan half of
            what the inventory raises as `ingredients-unassigned`. A piece is
            briefed strictly on what it claims, so anything sitting here produces
            nothing at all until someone assigns it, which is a fact about the
            plan that belongs on the plan rather than only inside a picker. */}
        <div className="bi-detail-section">
          <div className="bi-detail-section__h">
            Unclaimed material
            {!poolLoading && !poolErr && pool && <span className="bi-conf">{pool.length}</span>}
            {/* The same picker the header opens, offered where what it adds to
                is on screen. */}
            <button className="bi-btn bi-btn--sm" style={{ marginLeft: "auto" }} onClick={() => setAddKw(true)}><I name="target" size={14} />Add keywords</button>
          </div>
          <div className="bi-detail-section__body" style={{ paddingTop: 16, paddingBottom: 18 }}>
            {/* What the last pool write did, said beside the pool it changed.
                Shown above the loading branch so a removal that emptied the
                pool still gets to say so. */}
            {poolNote && (
              <div className="research-cost-note" style={{ marginBottom: 8, color: poolNote.tone === "error" ? "var(--danger)" : undefined }}>
                {poolNote.text} <button className="bi-btn bi-btn--sm" onClick={() => setPoolNote(null)}>Dismiss</button>
              </div>
            )}
            {poolLoading
              ? <div className="research-progress-read"><span className="ml-spinner ml-spinner--sm" />Reading what this plan holds unclaimed…</div>
              : poolErr
                ? <div className="ds-strip ds-strip--error"><I name="alert-triangle" size={14} /><span>{poolErr}</span><button className="bi-btn bi-btn--sm" onClick={loadPool}>Retry</button></div>
                : !pool
                  ? <div className="research-cost-note">No active site, so this plan's unclaimed material was not read.</div>
                  : pool.length === 0
                    ? (
                      /* Empty is two facts, and which one it is depends on what
                         the pieces claim. With the pieces unread, only the first
                         half is known and the row says exactly that much. */
                      <div className="research-cost-note">
                        {items === null
                          ? "Nothing is waiting to be claimed. The pieces could not be read, so whether this plan holds anything at all cannot be answered here."
                          : emptyPoolText(items)}
                      </div>
                    )
                    : (
                      <>
                        <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
                          {countText(pool)} in this plan that no piece has claimed. A piece is briefed strictly on what it claims, so this material is written into nothing until a piece takes it: open Material on the piece it belongs to.
                        </div>
                        <div className="ds-card__sample" style={{ marginTop: 8 }}>
                          {/* Keyword chips carry a remove control, because this
                              is where keywords are added from and the add needs
                              its undo in the same place. Other kinds arrived by
                              other routes (a piece released them, a suggestion
                              wrote them) and are released the way they came. */}
                          {pool.map(c => {
                            const k = KEY(c.kind, c.ref_id);
                            return (
                              <span className="ds-chip" key={k} title={`${KIND_LABEL[c.kind] || c.kind}: ${c.ref_label || c.ref_id}`}>
                                {c.ref_label || `#${c.ref_id}`}
                                {c.kind === "keyword" && (
                                  <button className="bi-iconbtn" title="Remove from this plan" disabled={poolBusy === k}
                                    style={{ width: 16, height: 16, marginLeft: 4, verticalAlign: "middle" }}
                                    onClick={() => removePoolIngredient(c)}>
                                    {poolBusy === k ? <span className="ml-spinner ml-spinner--sm" /> : <I name="x" size={11} />}
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>
                        {pool.some(c => !OFFERED_KINDS.has(c.kind)) && (
                          <p className="research-cost-note">Page references count here because the plan holds them, but no brief reads one, so Material does not offer them for a piece to claim.</p>
                        )}
                      </>
                    )}
          </div>
        </div>

        {/* What the pieces inherit */}
        <div className="bi-detail-section">
          <div className="bi-detail-section__h">Defaults every piece inherits</div>
          <div className="bi-detail-section__body">
            <div className="bi-detail-row"><span className="bi-detail-row__l">Audience · tone</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{plan.audience || "-"} · {plan.tone || "-"}</span></div>
            {/* What the plan produces comes first in this list, because every
                row under it is read differently depending on the answer. */}
            <div className="bi-detail-row"><span className="bi-detail-row__l">Produces</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{planOpt.label}, which every new piece starts as{targetSource(plan._raw) === "chosen" ? ", chosen rather than read off anything" : ""}</span></div>
            {planOpt.kind === "post"
              ? <div className="bi-detail-row"><span className="bi-detail-row__l">Categories · tags</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{plan.cats || "-"} · {plan.tags || "-"}</span></div>
              : <div className="bi-detail-row"><span className="bi-detail-row__l">Categories · tags</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{plan.cats || plan.tags
                  ? `${plan.cats || "-"} · ${plan.tags || "-"}, still held and not used: nothing files a ${planOpt.noun} under them`
                  : `None, and nothing files a ${planOpt.noun} under them`}</span></div>}
            <div className="bi-detail-row"><span className="bi-detail-row__l">Default word count</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{plan.words ? `${plan.words} words, unless a piece sets its own` : "1500 words, the generator's fallback"}</span></div>
            <div className="bi-detail-row"><span className="bi-detail-row__l">Brief</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{plan.brief || "-"}</span></div>
            <div className="bi-detail-row"><span className="bi-detail-row__l">Target URL</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{plan.target || "-"}</span></div>
            <div className="bi-detail-row"><span className="bi-detail-row__l">Due</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{plan.due || "-"}</span></div>
            {plan.legacyKw && (
              <div className="bi-detail-row"><span className="bi-detail-row__l">Keyword on the plan</span><span className="bi-detail-row__v" style={{ fontWeight: 400 }}>{plan.legacyKw}, from before plans had pieces. Only used by a piece that has no keyword of its own.</span></div>
            )}
          </div>
        </div>

        {editPiece && window.MlBiModals && <PieceModal planId={plan.id} plan={plan} piece={editPiece.piece} siteId={siteId} publish={publish} onClose={() => setEditPiece(null)} onSaved={after} />}
        {material && window.MlBiModals && (
          <MaterialModal plan={plan} piece={material} items={list} siteId={siteId}
            pool={pool} poolErr={poolErr} poolLoading={poolLoading} onRetryPool={loadPool}
            onClose={() => setMaterial(null)} onSaved={after} />
        )}
        {/* The same picker with no piece: PLAN mode, adding to the pool above. */}
        {addKw && window.MlBiModals && (
          <MaterialModal plan={plan} piece={null} items={list} siteId={siteId}
            pool={pool} poolErr={poolErr} poolLoading={poolLoading} onRetryPool={loadPool}
            onClose={() => setAddKw(false)} onSaved={after} onReport={setPoolNote} />
        )}
        {gen && window.MlBiModals && (
          <GeneratePieceModal plan={plan} piece={gen} siteId={siteId} onClose={() => setGen(null)}
            onDrafted={(article, notice, link) => { setGen(null); onDraft(article, notice, link); }} />
        )}
        {confirm && confirm.kind === "piece" && window.MlBiModals && (
          <window.MlBiModals.ConfirmDialog danger title={`Delete "${confirm.item.title}"?`}
            body="The piece goes. What it claimed returns to the plan, and anything already published in WordPress stays untouched."
            confirmLabel="Delete piece" onClose={() => setConfirm(null)} onConfirm={() => removePiece(confirm.item)} />
        )}
        {confirm && confirm.kind === "plan" && window.MlBiModals && (
          <window.MlBiModals.ConfirmDialog danger title={`Delete "${plan.title}"?`}
            body="The plan and all of its pieces are removed here, and their ingredients go back to the banks. Anything already published in WordPress stays untouched."
            confirmLabel="Delete plan" onClose={() => setConfirm(null)} onConfirm={() => { setConfirm(null); onDelete(plan.id); }} />
        )}
      </div>
    );
  }

  // onOpenDraft / onDraft come from the shell: the planner does not own
  // navigation, so without them the draft buttons stay out of the way rather
  // than pretending to work.
  // `detailId` / `onDetail`: which plan is open comes from the URL (see the
  // location convention in main.jsx), so a plan is a place that survives a
  // refresh and that Back leaves for the table.
  /* THE SWITCHER IS A FILTER WITH AN ALL POSITION (docs/MODULE-SCOPE.md 1.1).
     `site` is the ANCHOR CONNECTION, `sectionId` is the filter and is null at
     All, `sections` is this website's pieces. A plan belongs to the section
     that will PUBLISH the piece, which is why this module sits at the section
     at all (3.12d: a plan that cannot say where the piece will be published is
     not a plan), so at All it ENUMERATES with a Section column and no plan ever
     loses its section: every read, patch, delete and edit of a plan is
     addressed to that plan's own connection.

     At one section nothing about this screen changes. */
  function ContentPlanner({ site, sectionId, sections, onOpenDraft, onDraft, detailId, onDetail }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    // Where each kind would be published on THIS connection, read once here and
    // handed to every chooser below rather than fetched per modal: three open
    // forms asking the same question could answer it three different ways while
    // all three were on screen.
    const publish = usePublishTargets(site);
    const [stage, setStage] = useState("all");
    const [suggesting, setSuggesting] = useState(false);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [suggestErr, setSuggestErr] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [propOpen, setPropOpen] = useState(false);
    const [proposing, setProposing] = useState(false);
    const [proposals, setProposals] = useState([]);
    const [propErr, setPropErr] = useState(null);
    const [propMeta, setPropMeta] = useState(null);
    const [propRun, setPropRun] = useState(null);
    const [accepting, setAccepting] = useState(null);
    const propPoll = useRef(null);
    const [form, setForm] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const [sel, setSel] = useState([]);
    const [bulkStage, setBulkStage] = useState("idea");
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkErr, setBulkErr] = useState(null);
    const [editing, setEditing] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [viewMode, setViewMode] = useState("grid");
    // Behind the same detail/setDetail pair every call site below already
    // used, so nothing else in here changed. The local state is the fallback
    // for a host that does not own the location.
    const [localDetail, setLocalDetail] = useState(null);
    const detail = onDetail ? (detailId == null ? null : { id: detailId }) : localDetail;
    const setDetail = onDetail ? ((d) => onDetail(d == null ? null : d.id)) : setLocalDetail;
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    // The planner reading the banks itself: the cheap status line, the last
    // sweep, and what is being decided right now.
    const [discStatus, setDiscStatus] = useState(null);
    const [discStatusErr, setDiscStatusErr] = useState(null);
    const [discOpen, setDiscOpen] = useState(false);
    const [discRun, setDiscRun] = useState(null);      // the sweep record, or null when unread
    const [discErr, setDiscErr] = useState(null);
    const [sweeping, setSweeping] = useState(false);
    const [discBusy, setDiscBusy] = useState(null);    // key of the proposal being decided
    // Discovery strip: what it flagged and what this screen could not do with it.
    const [invKey, setInvKey] = useState(0);
    const [flagged, setFlagged] = useState(null);
    const [note, setNote] = useState(null);
    const listRef = useRef(null);
    // The All position of the switcher: no piece named, so the subject is the
    // whole website.
    const atAll = sectionId == null;
    const sectionList = Array.isArray(sections) ? sections : [];

    const load = useCallback(async () => {
      if (siteId == null) { setPlans([]); setLoading(false); return; }
      setLoading(true);
      // `scope=website` is the All position, and it is an explicit opt in on
      // the route rather than what an absent parameter means: that read is also
      // the assistant's and the Reports module's, and widening it by default
      // would rescope callers that never asked.
      const q = atAll ? `/content-plans?site_id=${siteId}&scope=website` : `/content-plans?site_id=${siteId}`;
      try { const rows = await api(q); setPlans((Array.isArray(rows) ? rows : []).map(mapPlan)); }
      catch { setPlans([]); }
      finally { setLoading(false); }
    }, [siteId, atAll]);
    useEffect(() => { load(); }, [load]);
    // Every write below changes what the strip counts (a plan with no ingredients,
    // a piece past the idea stage with no draft), so the reload that refreshes the
    // table refreshes the signals with it.
    const reload = useCallback(async () => { await load(); setInvKey(k => k + 1); }, [load]);
    // What the planner has looked at, and what is left. Counts only, so this is
    // cheap enough to run on every visit; the proposals themselves are only
    // fetched when the panel is opened.
    const loadDiscStatus = useCallback(async () => {
      if (siteId == null) { setDiscStatus(null); setDiscStatusErr(null); return; }
      try { setDiscStatus(await api(`/content-plans/discover/status?site_id=${siteId}`)); setDiscStatusErr(null); }
      catch (e) {
        // A status that could not be read must not render as a planner that has
        // never looked: one of those says there is nothing waiting, the other
        // says nobody knows.
        setDiscStatus(null);
        setDiscStatusErr(e.message || "Could not read what the planner has already looked at.");
      }
    }, [siteId]);
    useEffect(() => { loadDiscStatus(); }, [loadDiscStatus]);

    // A flag names plan ids from one site; carrying it into another would filter
    // by ids that mean nothing there.
    useEffect(() => { setFlagged(null); setNote(null); setDiscRun(null); setDiscOpen(false); setDiscErr(null); }, [siteId]);
    useEffect(() => () => { if (propPoll.current) propPoll.current(); }, []);

    /* THE CONNECTION A WRITE TO ONE PLAN GOES TO. The row's own, with the
       anchor as the fallback only for a server that sent none, which is the per
       connection read where they are the same number anyway. The switcher's id
       is never used for a plan of another section: the route matches
       `id = ? AND site_id = ?`, so it would match nothing and the screen would
       read as "that plan is gone" (MODULE-SCOPE 8.6, where the To-do list had
       exactly this defect). */
    const connOf = (plan) => (plan && plan.planSiteId != null ? plan.planSiteId : siteId);
    const connOfId = (id) => connOf(plans.find(x => x.id === id));
    const setPlanStage = async (id, val) => {
      const conn = connOfId(id);
      setPlans(ps => ps.map(p => p.id === id ? { ...p, stage: val } : p));   // optimistic
      if (conn != null) { try { await api(`/content-plans/${id}?site_id=${conn}`, { method: "PATCH", body: JSON.stringify({ status: STAGE_OUT[val] || val }) }); } catch { await load(); } }
      setInvKey(k => k + 1);
    };
    const delPlan = async (id) => { const conn = connOfId(id); if (conn == null) return; try { await api(`/content-plans/${id}?site_id=${conn}`, { method: "DELETE" }); setDetail(null); await reload(); } catch { /* */ } };

    // "Review them" means putting exactly the rows the headline counted on
    // screen. Every planner signal carries content_plans ids, including
    // ingredients-double-claimed: its headline counts contested ITEMS but its
    // ids are the plans contesting them, because those are the rows this screen
    // can show. The item names are on the card.
    const openSignal = (sig) => {
      // Count the PLANS, not sig.count: on the double-claim signal the headline
      // counts contested items, and reporting that as a number of plans would
      // be a different figure presented as the same one.
      const wanted = (sig.ids || []).length;
      const ids = (sig.ids || []).filter(id => plans.some(p => p.id === id));
      if (!ids.length) {
        setFlagged(null);
        setNote({ tone: "error", text: `The ${wanted} plan${wanted === 1 ? "" : "s"} behind that signal are not in the list on screen. Refresh and try again.` });
        return;
      }
      setNote(null);
      setStage("all");            // a stage tab would hide part of what was flagged
      setFlagged({ ids, title: sig.title, count: wanted });
      if (listRef.current) listRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const toggleSel = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    // No bulk endpoint exists, so these walk the selection one call at a time.
    // Whatever fails stays selected and is named, so the bar never claims a run
    // it did not finish; the reload afterwards is the source of truth.
    const runBulk = async (label, call) => {
      if (!sel.length || bulkBusy) return;
      setBulkBusy(true); setBulkErr(null);
      const ids = sel, failed = [];
      let firstMsg = "";
      for (const id of ids) {
        try { await call(id); } catch (e) { failed.push(id); firstMsg = firstMsg || (e && e.message) || ""; }
      }
      await reload();
      setSel(failed);
      setBulkErr(failed.length ? `${failed.length} of ${ids.length} could not be ${label}${firstMsg ? ": " + firstMsg : "."}` : null);
      setBulkBusy(false);
    };
    const bulkSetStage = () => runBulk("updated", (id) => api(`/content-plans/${id}?site_id=${connOfId(id)}`, { method: "PATCH", body: JSON.stringify({ status: STAGE_OUT[bulkStage] || bulkStage }) }));
    const bulkDelete = () => { setConfirm(null); return runBulk("deleted", (id) => api(`/content-plans/${id}?site_id=${connOfId(id)}`, { method: "DELETE" })); };

    const suggest = async () => {
      if (siteId == null) return;
      setSuggestOpen(true); setSuggesting(true); setSuggestions([]); setSuggestErr(null);
      try {
        const res = await api(`/content-plans/suggest`, { method: "POST", body: JSON.stringify({ site_id: siteId, count: 6 }) });
        setSuggestions(((res && res.suggestions) || []).map((s, i) => ({ id: "s" + i, title: s.title, type: optionOfPageType(s.page_type || "post").label, kw: s.primary_keyword || "", brief: s.brief || "", cats: arr(s.categories).join(", "), tags: arr(s.tags).join(", "), _raw: s })));
      } catch (e) {
        // An empty list and a failed call are not the same thing. The route
        // already distinguishes "nothing captured to plan from" (200 with a
        // note) from an unreadable model reply (502), so swallowing the error
        // here would throw that away and render both as "no suggestions".
        setSuggestions([]);
        setSuggestErr(e.message || "Could not get suggestions.");
      }
      finally { setSuggesting(false); }
    };
    // Propositions: the AI groups what is sitting FREE in the banks into pieces.
    //
    // Different from Suggest, which reads the captured backlog and writes plans
    // whose ingredients had to be matched back by name. Here every item is sent
    // with its row id, the reply may only refer to ids it was given, and
    // anything else is discarded server-side. So a proposal on screen is made
    // of rows that exist, and accepting it links them exactly.
    const propose = async () => {
      if (siteId == null) return;
      setPropOpen(true); setProposing(true); setProposals([]); setPropErr(null); setPropMeta(null); setPropRun(null);
      const runId = newRunId();
      propPoll.current = pollRun(runId, setPropRun);
      try {
        const res = await api(`/inventory/propose`, { method: "POST", body: JSON.stringify({ site_id: siteId, count: 5, run_id: runId }) });
        setProposals((res && res.proposals) || []);
        setPropMeta({
          dropped: ((res && res.dropped) || []).length,
          catalogue: (res && res.catalogue_size) || 0,
          note: (res && res.note) || null,
          truncated: !!(res && res.truncated),
        });
      } catch (e) {
        setProposals([]);
        setPropErr(e.message || "Could not get proposals.");
      } finally {
        setProposing(false);
        if (propPoll.current) { propPoll.current(); propPoll.current = null; }
      }
    };

    // Accepting writes an ordinary plan, then the piece that plan is for, so
    // what comes back is already a body of work with one article in it rather
    // than a title with material behind it and nothing to generate from. The
    // ingredients go on the PIECE: an ingredient the plan holds but no piece
    // claims never reaches a brief.
    const acceptProposal = async (pr, idx) => {
      if (siteId == null) return;
      setAccepting(idx); setPropErr(null);
      try {
        // The plan declares what it produces, from the proposal's own answer
        // rather than from the create route's default. A plan left at the
        // default while its only piece is a service page would contradict the
        // one piece it was made for, and every later piece would inherit the
        // contradiction.
        const created = await api(`/content-plans`, { method: "POST", body: JSON.stringify({
          site_id: siteId, title: pr.title, brief: pr.brief || null,
          ...(VALID_PAGE_TYPES.has(pr.page_type) ? { page_type: pr.page_type } : {}),
          status: "idea", source: "ai_propose",
        }) });
        const planId = created && created.id;
        if (!planId) throw new Error("the plan was saved but came back without an id");
        try {
          await api(`/content-plans/${planId}/items`, { method: "POST", body: JSON.stringify({
            site_id: siteId, title: pr.title, page_type: pr.page_type,
            primary_keyword: pr.primary_keyword || null,
            ingredients: pr.ingredients,
          }) });
        } catch (e) {
          // The plan exists now, so the card has to go or a retry would create a
          // second one. What did not land is named instead of being swallowed.
          setProposals(list => list.filter((_, i) => i !== idx));
          setPropErr(`"${pr.title}" was added as a plan, but its piece and material could not be attached: ${e.message || "the request failed"}. Open the plan and add the piece there.`);
          await reload();
          return;
        }
        setProposals(list => list.filter((_, i) => i !== idx));
        await reload();
      } catch (e) {
        setPropErr(`Could not add "${pr.title}": ${e.message || "the request failed"}`);
      } finally { setAccepting(null); }
    };

    // ── The planner reading the banks itself ────────────────
    // Free and deterministic: the server matches free bank items on facets the
    // rows already carry, so a sweep spends nothing and proposes only material
    // that exists. Nothing is written until one of these cards is approved.
    const sweep = async () => {
      if (siteId == null || sweeping) return;
      setDiscOpen(true); setSweeping(true); setDiscErr(null);
      try {
        setDiscRun(await api(`/content-plans/discover`, { method: "POST", body: JSON.stringify({ site_id: siteId }) }));
      } catch (e) {
        // The previous run stays on screen: a sweep that failed has not
        // invalidated the proposals already waiting.
        setDiscErr(e.message || "The sweep failed, so nothing new was read.");
      } finally { setSweeping(false); await loadDiscStatus(); }
    };

    // Reopening the panel shows the sweep that already ran rather than running
    // another one, which would weigh a second batch of rows before the user has
    // dealt with the first.
    const openDiscovery = async () => {
      setDiscOpen(true);
      if (siteId == null || discRun || sweeping) return;
      setDiscErr(null);
      try { setDiscRun(await api(`/content-plans/discover?site_id=${siteId}`)); }
      catch (e) { setDiscRun(null); setDiscErr(e.message || "Could not read the last sweep."); }
    };

    // A card the server no longer knows about, taken off screen so the panel
    // does not go on offering something that has already happened.
    const dropCard = (key) => setDiscRun(r => (r ? { ...r, proposals: (r.proposals || []).filter(p => p.key !== key), waiting: Math.max(0, (r.waiting || 0) - 1) } : r));

    // Recording the decision is a separate request from making it, because
    // creating the plan is the thing that matters and it goes through the one
    // route that creates plans. Returns true when the sweep took the decision.
    const decide = async (pr, outcome) => {
      try {
        setDiscRun(await api(`/content-plans/discover/decide`, { method: "POST", body: JSON.stringify({ site_id: siteId, key: pr.key, outcome }) }));
        return true;
      } catch { dropCard(pr.key); return false; }
    };

    // Approving writes an ordinary plan and then its first piece, exactly as
    // accepting an AI proposal does: the sweep decides what is WORTH planning,
    // it does not become a second way of creating one. The ingredients go on the
    // PIECE, because material a plan holds and no piece claims reaches no brief.
    const takeProposal = async (pr) => {
      if (siteId == null || discBusy) return;
      setDiscBusy(pr.key); setDiscErr(null);
      try {
        const created = await api(`/content-plans`, { method: "POST", body: JSON.stringify({
          site_id: siteId, title: pr.title,
          // As above: the plan says what it produces, taken from the proposal.
          ...(VALID_PAGE_TYPES.has(pr.page_type) ? { page_type: pr.page_type } : {}),
          status: "idea", source: "discover",
        }) });
        const planId = created && created.id;
        if (!planId) throw new Error("the plan was saved but came back without an id");
        let attached = true;
        try {
          await api(`/content-plans/${planId}/items`, { method: "POST", body: JSON.stringify({
            site_id: siteId, title: pr.title, page_type: pr.page_type,
            ingredients: pr.ingredients,
          }) });
        } catch (e) {
          attached = false;
          setDiscErr(`"${pr.title}" was added as a plan, but its piece and material could not be attached: ${e.message || "the request failed"}. Open the plan and add the piece there.`);
        }
        // The plan exists either way, so the card goes either way: leaving it
        // would invite a second plan with the same title on the next click.
        const recorded = await decide(pr, "planned");
        if (!recorded && attached) {
          setDiscErr(`"${pr.title}" is now a plan, but the sweep could not record that. It may be proposed again next time.`);
        }
        await reload();
        await loadDiscStatus();
      } catch (e) {
        setDiscErr(`Could not add "${pr.title}": ${e.message || "the request failed"}`);
      } finally { setDiscBusy(null); }
    };

    // Dismissing changes nothing about the banks: the rows stay where they are,
    // and only this proposal stops being offered.
    const dismissProposal = async (pr) => {
      if (siteId == null || discBusy) return;
      setDiscBusy(pr.key); setDiscErr(null);
      const recorded = await decide(pr, "dismissed");
      if (!recorded) setDiscErr(`"${pr.title}" was taken off screen, but the sweep could not record the dismissal, so it may come back.`);
      setDiscBusy(null);
      await loadDiscStatus();
    };

    // One at a time. In parallel a failure would race the reload and the panel
    // could not say which card did not make it.
    const addAll = async () => { for (const s of [...suggestions]) { await addSuggestion(s); } };

    const addSuggestion = async (s) => {
      if (siteId == null) return;
      const r = s._raw || {};
      try {
        // The plan carries the defaults and the ingredient links; the piece
        // carries the article. `topics` is what the plan CONSUMES from the bank
        // and `outline` is the article's own sections: the suggester used to
        // return the sections under `topics`, so accepting a suggestion stored
        // headings as bank references that resolved to nothing. mark_used
        // carries the exact provided strings, which is what turns into edges.
        const sType = VALID_PAGE_TYPES.has(r.page_type) ? r.page_type : "post";
        const created = await api(`/content-plans`, { method: "POST", body: JSON.stringify({
          site_id: siteId, title: r.title, page_type: sType,
          primary_keyword: r.primary_keyword || null, secondary_keywords: arr(r.secondary_keywords),
          topics: arr(r.topics), outline: arr(r.outline), mark_used: r.mark_used || undefined,
          // Only a plan producing posts is filed under categories and tags, so a
          // suggestion for a page does not carry a pair nothing files it under.
          ...(optionOfPageType(sType).kind === "post" ? { categories: arr(r.categories), tags: arr(r.tags) } : {}),
          brief: r.brief || null,
          status: "idea", source: "ai_suggest",
        }) });
        const planId = created && created.id;
        if (!planId) throw new Error("the plan was saved but came back without an id");
        // The plan's edges were resolved from names by the server, so the piece
        // is created without ingredients and claims them from the pool in
        // "Material". Its keyword is carried over, which is what lets it
        // generate even before anything is claimed.
        try {
          await api(`/content-plans/${planId}/items`, { method: "POST", body: JSON.stringify({
            site_id: siteId, title: r.title, page_type: sType,
            primary_keyword: r.primary_keyword || null,
          }) });
        } catch (e) {
          // The plan landed, so the card goes either way: leaving it would
          // invite a second plan with the same title on the next click.
          setSuggestions(list => list.filter(x => x.id !== s.id));
          setSuggestErr(`"${r.title}" was added as a plan, but its first piece could not be created: ${e.message || "the request failed"}. Open the plan and add the piece there.`);
          await reload();
          return;
        }
        setSuggestions(list => list.filter(x => x.id !== s.id)); await reload();
      } catch (e) {
        // Silence here meant a card that stayed on screen with no reason given,
        // which reads as the click not registering.
        setSuggestErr(`Could not add "${r.title || "that plan"}": ${e.message || "the request failed"}`);
      }
    };

    /* These are counts of the ROWS ON SCREEN and they stay, at both positions,
       because they are what the stage tabs filter by and the list is one
       database read rather than a union over three CMSs. What does NOT appear
       at All is a figure about the website: the panel below enumerates per
       piece and says there is no total. */
    const counts = { all: plans.length }; STAGES.forEach(([id]) => counts[id] = plans.filter(p => p.stage === id).length);
    const rows = flagged ? plans.filter(p => flagged.ids.includes(p.id))
      : stage === "all" ? plans : plans.filter(p => p.stage === stage);

    // The detail view, after every hook above has run. An early return before
    // them would change the number of hooks between renders, which React treats
    // as a crash rather than a branch.
    // A link straight to one plan arrives before the table it belongs to, so
    // the plan it names is legitimately absent for a moment. Three answers,
    // never one: the plan, the wait, or a line saying it is not here. The blank
    // page this used to render covered all three, and for a deleted plan it
    // never stopped being blank.
    const detailPlan = detail && plans.find(x => x.id === detail.id);
    if (detailPlan) {
      return (
        <div className="bi-page">
          {/* The plan's OWN connection, not the switcher's: its pieces, its
              pool, its brief and every write to them are per connection reads,
              so a plan of another section opened from the All list would
              otherwise show an empty plan. */}
          <PlanDetail plan={detailPlan} siteId={connOf(detailPlan)} onBack={() => setDetail(null)} onStage={setPlanStage}
            onDelete={delPlan} onEdit={setEditing} onDraft={onDraft} onChanged={reload}
            /* The editor is opened on the PLAN's connection, not the
               switcher's: a linked post id belongs to the CMS the plan
               publishes to (MODULE-SCOPE 4.2, the editor's scope is the scope
               of the row that opened it). */
            onOpenDraft={onOpenDraft ? ((postId, title) => onOpenDraft(postId, title, connOf(detailPlan))) : null}
            publish={publish} />
          {editing && window.MlBiModals && <EditPlanModal plan={editing} siteId={connOf(editing)} publish={publish} onClose={() => setEditing(null)} onSaved={reload} />}
        </div>
      );
    }
    if (detail && loading) {
      return <div className="bi-page"><div className="research-progress-read"><span className="ml-spinner ml-spinner--sm" />Opening the plan…</div></div>;
    }
    // Falls through to the table below, with the reason above it.
    const planGone = !!detail;

    return (
      <div className="bi-page">
        <div className="bi-header">
          <div className="bi-header__titles"><h1 className="bi-title"><span className="bi-title__icon"><I name="calendar-days" size={20} /></span>Content Planner</h1></div>
          <div className="bi-header-actions">
            <div className="bi-viewtoggle" role="tablist" aria-label="View mode">
              <button className={viewMode === "grid" ? "on" : ""} title="Grid view" aria-pressed={viewMode === "grid"} onClick={() => setViewMode("grid")}><I name="grid" size={16} /></button>
              <button className={viewMode === "list" ? "on" : ""} title="List view" aria-pressed={viewMode === "list"} onClick={() => setViewMode("list")}><I name="list" size={16} /></button>
            </div>
            {/* A plan has to say which piece of the website will publish it,
                which is the whole reason this module sits at the section
                (3.12d). At All the switcher names no piece, so creating one
                would pick a CMS for the customer. The three buttons that CREATE
                a plan say to choose instead; everything that READS stays. */}
            <button className="bi-btn" disabled={atAll} title={atAll ? "Pick the piece of the website from the switcher first: a plan has to say where the piece will be published." : undefined} onClick={() => setForm(true)}><I name="plus" size={16} />New plan</button>
            {/* Free, so it is an ordinary button rather than an AI one: the
                sweep matches facets already in the database and spends nothing. */}
            <button className="bi-btn" disabled={sweeping} onClick={sweep}><I name="search" size={16} />{sweeping ? "Reading the banks..." : "Discover"}</button>
            <button className="bi-btn bi-btn--ai" disabled={atAll} title={atAll ? "Pick the piece of the website from the switcher first: a suggested plan still has to be published somewhere." : undefined} onClick={suggest}><I name="sparkles" size={16} />Suggest</button>
            <button className="bi-btn bi-btn--ai" disabled={proposing || atAll} title={atAll ? "Pick the piece of the website from the switcher first: a proposed plan still has to be published somewhere." : undefined} onClick={propose}><I name="shuffle" size={16} />{proposing ? "Proposing..." : "Propose from banks"}</button>
            <button className="bi-btn" onClick={() => { setSelectMode(s => !s); setSel([]); setBulkErr(null); }}>{selectMode ? <><I name="x" size={16} />Cancel</> : <><I name="check-square" size={16} />Select</>}</button>
            <button className="bi-btn" onClick={reload}><I name="refresh" size={16} />Refresh</button>
          </div>
        </div>
        <p className="research-intro">“The kitchen.” Assemble captured keywords, topics, FAQs and gaps into a plan, break it into <b>pieces</b>, and each piece becomes one published post.
          {atAll ? <> Every plan of this website is here, and each one names the piece that will publish it: the plan's stage, its pieces and its brief are all read and written through that piece.</> : null}
        </p>

        {/* ── What this answer is (MODULE-SCOPE 1.1) ──
            BUILT HERE rather than rendered from the server's own sentences, and
            that is the one place in this change where the client composes the
            report. GET /content-plans answers a BARE ARRAY by contract (the
            assistant reads it too), so there is no envelope to carry them. It
            is safe because there is nothing to find out: a plan is a row in
            this database, so the union is one statement, every piece of the
            website is in it by construction, and no piece can be unreachable.
            That is exactly what makes the Posts and Pages panels the server's
            job and this one not. */}
        {atAll && (
          <div className="bm-kw-panel" style={{ marginTop: 12, marginBottom: 14 }}>
            <div className="bm-kw-row">
              <span className="bm-kw-lab">The website</span>
              <span className="bm-kw-note">
                {plans.length} plan(s) on screen, from {sectionList.length} piece(s). Read in one database statement
                over the whole website, so every piece answered and none could be unreachable.
              </span>
            </div>
            {sectionList.map(sec => {
              const id = sec.site_id != null ? sec.site_id : sec.id;
              const mine = plans.filter(x => x.planSiteId === id).length;
              const crawl = sec.source === "crawl";
              return (
                <div className="bm-kw-row" key={id}>
                  <span className="bm-kw-chip">{sec.name}</span>
                  <span className="bm-kw-note">
                    {mine} plan(s) · {crawl ? "Plan, then hand over a brief" : "Publish"}
                    {crawl ? ": this piece has no CMS, so a plan targeting it is a brief for whoever builds the front end rather than a publish target." : ""}
                  </span>
                </div>
              );
            })}
            <p className="bm-kw-note">
              No total: a plan belongs to the piece that will publish it, and the pieces of this website publish
              through different CMSs, so a sum over them is not a figure anybody asked for.
            </p>
            {/* The 8.6 shape, said rather than hidden: the planner's own reads
                of the banks are per connection and this filter does not touch
                them. A filter that appears to narrow a panel it does not reach
                is worse than one that says where it stops. */}
            <p className="bm-kw-note">
              Discover and the signals below read the banks of <b>{(site && site.name) || "the anchor connection"}</b>
              {" "}only: a keyword register belongs to a market, which is a separate decision, so this filter does not
              reach them.
            </p>
          </div>
        )}
        {planGone && <div className="bi-subempty">That link points to a plan that is not here. It may have been deleted, or it belongs to another site.</div>}

        <div className="bi-stat-cards" style={{ gridTemplateColumns: "repeat(8, 1fr)" }}>
          {[["all", "All", "var(--primary)"], ...STAGES.map(([id, l]) => [id, l, STAGE_COLOR[id]])].map(([id, l, c]) => (
            <button key={id} className={"bi-stat-card bi-stat-card--clickable" + (!flagged && stage === id ? " bi-stat-card--active" : "")} onClick={() => { setStage(id); setFlagged(null); }} style={{ padding: "12px 12px" }}>
              <div className="bi-stat-card__top"><span className="bi-stat-card__dot" style={{ background: c }} /><span className="bi-stat-card__label" style={{ fontSize: 12 }}>{l}</span></div>
              <span className="bi-stat-card__value" style={{ fontSize: "var(--text-xl)" }}>{counts[id]}</span>
            </button>
          ))}
        </div>

        {/* Deterministic findings about the plans below, so the screen says what
            it is not showing. Only the `open` action is wired: `pieces-no-draft`
            carries PIECE ids and this list holds plans, so a generate button
            here could not put the rows it counted on screen, and generating is
            a per-piece decision made inside a plan with its brief in view. */}
        {window.MlDiscovery && (
          <window.MlDiscovery.DiscoveryStrip siteId={siteId} module="planner" actions={{ open: openSignal }} reloadKey={invKey} />
        )}
        {note && (
          <div className="research-cost-note" style={{ marginTop: 10, color: note.tone === "error" ? "var(--danger)" : undefined }}>
            {note.text} <button className="bi-btn bi-btn--sm" onClick={() => setNote(null)}>Dismiss</button>
          </div>
        )}

        {/* What the planner has already weighed, before anything is pressed.
            The whole point of the sweep is that material nobody noticed still
            gets considered, so the screen has to say how much of that there is,
            and it has to say when somebody last looked. */}
        {(discStatus || discStatusErr) && (
          <div className="research-cost-note" style={{ marginTop: 10 }}>
            {discStatusErr
              ? (
                <span style={{ color: "var(--danger)" }}>
                  {discStatusErr} <button className="bi-btn bi-btn--sm" onClick={loadDiscStatus}>Retry</button>
                </span>
              )
              : (
                <>
                  {discStatus.ran_before
                    ? `The planner last read your banks on ${whenText(discStatus.last_run_at) || "a date this browser cannot read"}. `
                    : "The planner has never read your banks. "}
                  {/* Two numbers, never one: what is waiting, and how much of it
                      a sweep could actually use. On a freshly imported bank
                      those are 1,934 and 12, and reporting only the second
                      describes a different site. */}
                  {(discStatus.backlog && discStatus.backlog.unseen > 0)
                    ? `${plural(discStatus.backlog.unseen, "item has", "items have")} never been weighed, ${discStatus.backlog.plannable} of them ready to plan now`
                      + (discStatus.backlog.arrived != null ? `, and ${discStatus.backlog.arrived} arrived since` : "")
                      + "."
                    : "Nothing in your banks is waiting to be weighed."}
                  {discStatus.waiting > 0 && (
                    <> {plural(discStatus.waiting, "proposal is", "proposals are")} waiting for you.{" "}
                      <button className="bi-btn bi-btn--sm" onClick={openDiscovery}>Show them</button></>
                  )}
                </>
              )}
          </div>
        )}

        {discOpen && (
          <div className="cp-suggest-panel">
            <div className="cp-suggest-panel__head">
              <div className="cp-suggest-panel__title"><I name="search" size={18} />Found in your banks</div>
              {!sweeping && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="bi-btn bi-btn--sm" onClick={sweep}><I name="refresh" size={14} />Read again</button>
                  <button className="bi-btn bi-btn--sm" onClick={() => setDiscOpen(false)}>Dismiss</button>
                </div>
              )}
            </div>
            {discErr && <div className="ds-strip ds-strip--error" style={{ marginBottom: 12 }}><I name="alert-triangle" size={14} /><span>{discErr}</span><button className="bi-btn bi-btn--sm" onClick={() => setDiscErr(null)}>Dismiss</button></div>}
            {sweeping
              ? <div className="research-progress-read" style={{ padding: "12px 0" }}><span className="ml-spinner ml-spinner--sm" />Reading the keywords and topics nobody has weighed yet. Nothing is being spent.</div>
              : !discRun
                ? <div className="research-cost-note" style={{ padding: "12px 0" }}>The last sweep was not read, so nothing is shown here. Read the banks again to see what they hold.</div>
                : discRun.never_run
                  ? <div className="research-cost-note" style={{ padding: "12px 0" }}>The planner has not read your banks yet. Press "Read again" and it will propose what it would plan, without spending anything.</div>
                  : (
                    <>
                      {/* THE SWEEP'S OWN REPORT, first on the panel and folded
                          to one line that opens a drawer. The could-not-use
                          number stays in the line on purpose: a sweep that
                          quietly ignored most of a bank and reported "6
                          proposals" would be describing a bank nobody queried.
                          Everything the report said before the fold is in the
                          drawer, word for word. No Dismiss here, because this
                          block never had one: it lives and dies with the
                          panel. */}
                      <window.MlReportDrawer.ReportStrip
                        title={`Sweep: ${plural(discRun.fresh || 0, "new proposal", "new proposals")}${discRun.carried ? `, ${discRun.carried} waiting from before` : ""}${discRun.unusable && discRun.unusable.total > 0 ? `, ${discRun.unusable.total} of ${discRun.unseen_total} could not be planned` : ""}`}
                        tone={discRun.unusable && discRun.unusable.total > 0 ? "problem" : "opportunity"}
                      >
                        {discRun.unusable && discRun.unusable.total > 0 && (
                          <>
                            <div className="ds-strip ds-strip--error" style={{ marginBottom: 8 }}>
                              <I name="alert-triangle" size={14} />
                              <span>
                                {plural(discRun.unusable.total, "item", "items")} of the {discRun.unseen_total} the planner has not weighed could not be planned at all.
                                {" "}{plural((discRun.proposals || []).length, "proposal", "proposals")} below {(discRun.proposals || []).length === 1 ? "is" : "are"} not a report on your banks.
                              </span>
                            </div>
                            <div className="bi-card__meta" style={{ marginBottom: 12 }}>
                              {unusableRows(discRun.unusable).map(r => (
                                <div className="bi-meta-row" key={r.reason}>
                                  <span className="bi-meta-row__l">{r.count}</span>
                                  <span className="bi-meta-row__v" style={{ fontWeight: 400 }}>
                                    {r.label} ({r.per.map(p => `${p[1]} ${p[0]}`).join(", ")})
                                  </span>
                                </div>
                              ))}
                            </div>
                            {discRun.blocked_note && (
                              <p className="research-cost-note" style={{ marginBottom: 12 }}>{discRun.blocked_note}</p>
                            )}
                          </>
                        )}

                        {/* What this run read and what it left. A capped sweep
                            that looks finished is the same failure in a new place. */}
                        <p className="research-cost-note" style={{ marginBottom: 12 }}>
                          {plural(discRun.fresh || 0, "proposal", "proposals")} from this sweep
                          {discRun.carried ? `, ${discRun.carried} still waiting from before` : ""}.
                          {discRun.remaining
                            ? ` ${SWEPT.map(([kind, label]) => `${Number(discRun.remaining[kind] || 0)} ${label}`).join(" and ")} are ready to plan and were not reached this time.`
                            : " How many are left was not counted, so this run cannot say whether it reached the end."}
                          {(discRun.refused || []).length > 0
                            ? ` ${plural(discRun.refused.length, "item", "items")} changed while the sweep was running and were left for next time.`
                            : ""}
                        </p>
                      </window.MlReportDrawer.ReportStrip>

                      {(discRun.proposals || []).length === 0
                        ? (
                          <div className="research-cost-note" style={{ padding: "12px 0" }}>
                            {/* Three different nothings, and only one of them
                                means the planner is finished. */}
                            {discRun.unusable && discRun.unusable.total > 0 && !(discRun.remaining && (Number(discRun.remaining.keyword || 0) + Number(discRun.remaining.topic || 0)) > 0)
                              ? "Nothing could be proposed: every item still waiting is one of the reasons above."
                              : (discRun.unseen_total || 0) === 0
                                ? "The planner has weighed everything in your banks. Nothing new to propose until something arrives."
                                : "Nothing new was proposed this time. What is left is either already in a proposal you have decided about or was not reached, so read the banks again."}
                            {" "}{(discRun.decided && (discRun.decided.planned || discRun.decided.dismissed))
                              ? `${discRun.decided.planned} planned and ${discRun.decided.dismissed} dismissed from this sweep.`
                              : ""}
                          </div>
                        )
                        : (
                          <div className="cp-suggest-grid">{(discRun.proposals || []).map(pr => (
                            <div className="bi-card" key={pr.key} style={{ cursor: "default" }}>
                              <div className="bi-card-head"><div className="bi-card__name" style={{ fontSize: "var(--text-sm)" }}>{pr.title}</div></div>
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                <span className="cp-pagetype" title={optionOfPageType(pr.page_type).sub}>{optionOfPageType(pr.page_type).label}</span>
                                {pr.seed && <span className="research-tag research-tag--intent" style={{ textTransform: "none" }}>{KIND_LABEL[pr.seed.kind] || pr.seed.kind}: {pr.seed.label}</span>}
                                {pr.carried && <span className="cluster-tag">waiting from an earlier sweep</span>}
                              </div>
                              <p className="research-row-desc" style={{ marginTop: 4 }}>
                                {matchedText(pr.matched_on)
                                  ? `Everything free in your banks sharing this item's ${matchedText(pr.matched_on)}.`
                                  : "Assembled from this item alone."}
                                {pr.capped ? " More matched than one article can hold, so the strongest were taken." : ""}
                              </p>
                              {/* Named, not counted: approving commits these
                                  exact rows, so the user sees which ones. */}
                              <div className="ds-card__sample">
                                {arr(pr.ingredients).map(ing => (
                                  <span className="ds-chip" key={KEY(ing.kind, ing.ref_id)} title={`${KIND_LABEL[ing.kind] || ing.kind}: ${ing.ref_label}${ing.matched_on ? `, matched on ${ing.matched_on}` : ""}`}>{ing.ref_label || `#${ing.ref_id}`}</span>
                                ))}
                              </div>
                              {/* Said on the card, before the click: a piece
                                  with no keyword cannot generate a draft, and a
                                  card that stayed quiet about it would let the
                                  user find out after the plan exists. */}
                              {!pr.has_keyword && (
                                <p className="research-cost-note" style={{ marginTop: 4 }}>No keyword in this one, so the piece it creates cannot generate a draft until one is attached.</p>
                              )}
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
                                <button className="bi-btn bi-btn--sm" disabled={discBusy === pr.key} onClick={() => dismissProposal(pr)}><I name="x" size={14} />Dismiss</button>
                                <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={discBusy === pr.key} onClick={() => takeProposal(pr)}>
                                  <I name="plus" size={14} />{discBusy === pr.key ? "Adding..." : `Add plan with ${plural(arr(pr.ingredients).length, "item", "items")}`}
                                </button>
                              </div>
                            </div>
                          ))}</div>
                        )}
                    </>
                  )}
          </div>
        )}

        {propOpen && (
          <div className="cp-suggest-panel">
            <div className="cp-suggest-panel__head">
              <div className="cp-suggest-panel__title"><I name="shuffle" size={18} />Proposed from your banks</div>
              {!proposing && <button className="bi-btn bi-btn--sm" onClick={() => setPropOpen(false)}>Dismiss</button>}
            </div>
            {propErr && <div className="ds-strip ds-strip--error" style={{ marginBottom: 12 }}><I name="alert-triangle" size={14} /><span>{propErr}</span><button className="bi-btn bi-btn--sm" onClick={() => setPropErr(null)}>Dismiss</button></div>}
            {proposing
              ? (
                /* Milestones the server actually reached, not a timer. */
                <div style={{ padding: "12px 0" }}>
                  <div className="ml-progress-row" style={{ marginBottom: 10 }}>
                    <div className="ml-progress"><div className="ml-progress__bar ml-progress__bar--ai" style={{ width: (propRun ? propRun.pct : 5) + "%" }} /></div>
                    <span className="ml-progress-row__pct">{propRun ? propRun.pct : 5}%</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
                    <span className="ml-spinner ml-spinner--sm ml-spinner--ai" />
                    {propRun ? propRun.label : "Starting"}{propRun && propRun.note ? `: ${propRun.note}` : ""}
                  </div>
                </div>
              )
              : proposals.length === 0
                ? (propErr ? null : (
                  <div className="research-cost-note" style={{ padding: "12px 0" }}>
                    {(propMeta && propMeta.note) || "Nothing came back worth proposing from what is currently free."}
                  </div>
                ))
                : (
                  <>
                    {/* What the model was given and what was thrown away, so a
                        short list is explained rather than looking like a thin
                        answer. */}
                    {propMeta && (
                      <p className="research-cost-note" style={{ marginBottom: 12 }}>
                        Grouped from {propMeta.catalogue} free item{propMeta.catalogue === 1 ? "" : "s"}.
                        {propMeta.dropped ? ` ${propMeta.dropped} reference${propMeta.dropped === 1 ? " was" : "s were"} discarded for pointing at nothing in your banks.` : ""}
                        {propMeta.truncated ? " The reply was cut short, so there may be fewer here than asked for." : ""}
                      </p>
                    )}
                    <div className="cp-suggest-grid">{proposals.map((pr, i) => (
                      <div className="bi-card" key={pr.title + i} style={{ cursor: "default" }}>
                        <div className="bi-card-head"><div className="bi-card__name" style={{ fontSize: "var(--text-sm)" }}>{pr.title}</div></div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <span className="cp-pagetype" title={optionOfPageType(pr.page_type).sub}>{optionOfPageType(pr.page_type).label}</span>
                          {pr.primary_keyword && <span className="research-tag research-tag--intent" style={{ textTransform: "none" }}>{pr.primary_keyword}</span>}
                        </div>
                        {pr.why && <p className="research-row-desc" style={{ marginTop: 4, fontStyle: "italic" }}>{pr.why}</p>}
                        {pr.brief && <p className="research-row-desc" style={{ marginTop: 4 }}>{pr.brief}</p>}
                        {/* Named, not counted: the user is about to commit these
                            exact rows, so they get to see which ones. */}
                        <div className="ds-card__sample">
                          {pr.ingredients.map(ing => (
                            <span className="ds-chip" key={ing.kind + ing.ref_id} title={`${ing.kind}: ${ing.ref_label}`}>{ing.ref_label}</span>
                          ))}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
                          <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={accepting === i} onClick={() => acceptProposal(pr, i)}>
                            <I name="plus" size={14} />{accepting === i ? "Adding..." : `Add plan with ${pr.ingredients.length} item${pr.ingredients.length === 1 ? "" : "s"}`}
                          </button>
                        </div>
                      </div>
                    ))}</div>
                  </>
                )}
          </div>
        )}

        {suggestOpen && (
          <div className="cp-suggest-panel">
            <div className="cp-suggest-panel__head">
              <div className="cp-suggest-panel__title"><I name="sparkles" size={18} />Suggested plans</div>
              {!suggesting && <div style={{ display: "flex", gap: 8 }}><button className="bi-btn bi-btn--sm" onClick={() => setSuggestOpen(false)}>Dismiss</button>{suggestions.length > 0 && <button className="bi-btn bi-btn--sm bi-btn--primary" onClick={addAll}>Add all</button>}</div>}
            </div>
            {suggestErr && <div className="ds-strip ds-strip--error" style={{ marginBottom: 12 }}><I name="alert-triangle" size={14} /><span>{suggestErr}</span><button className="bi-btn bi-btn--sm" onClick={() => setSuggestErr(null)}>Dismiss</button></div>}
            {suggesting
              ? <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 0", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}><span className="ml-spinner ml-spinner--sm ml-spinner--ai" />Generating plan ideas from your captured topics and gaps...</div>
              : suggestions.length === 0
                ? (suggestErr
                    ? null
                    : <div className="research-cost-note" style={{ padding: "12px 0" }}>No suggestions right now: capture more keywords, topics and gaps first.</div>)
                : <div className="cp-suggest-grid">{suggestions.map(s => (
                  <div className="bi-card" key={s.id} style={{ cursor: "default" }}>
                    <div className="bi-card-head"><div className="bi-card__name" style={{ fontSize: "var(--text-sm)" }}>{s.title}</div></div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}><span className="cp-pagetype">{s.type}</span><span className="research-tag research-tag--intent" style={{ textTransform: "none" }}>{s.kw}</span></div>
                    <p className="research-row-desc" style={{ marginTop: 4 }}>{s.brief}</p>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}><span className="cluster-tag">{s.cats}</span><span className="cluster-tag">{s.tags}</span></div>
                    <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}><button className="bi-btn bi-btn--sm bi-btn--primary" onClick={() => addSuggestion(s)}><I name="plus" size={14} />Add</button></div>
                  </div>
                ))}</div>}
          </div>
        )}

        {form && <PlanForm onClose={() => setForm(false)} site={site} onSaved={reload} publish={publish} />}

        {selectMode && (
          <div className="bi-bulk-bar">
            {bulkBusy ? (
              <span className="research-progress-read"><span className="ml-spinner ml-spinner--sm" />Working through {sel.length} plan{sel.length === 1 ? "" : "s"}…</span>
            ) : (
              <>
                <span className="bi-bulk-bar__count">{sel.length} selected</span>
                <button className="bi-btn bi-btn--sm" onClick={() => setSel(rows.map(p => p.id))}><I name="check-square" size={14} />Select all ({rows.length})</button>
                <button className="bi-btn bi-btn--sm" onClick={() => { setSel([]); setBulkErr(null); }}>Clear</button>
                <span className="bi-bulk-bar__sep" />
                <FD label="Set status" value={bulkStage} opts={STAGES} colorOf={v => STAGE_COLOR[v]} onChange={setBulkStage} />
                <button className="bi-btn bi-btn--sm" disabled={!sel.length} onClick={bulkSetStage}>Set status</button>
                <button className="bi-btn bi-btn--sm bi-btn--danger" disabled={!sel.length} onClick={() => setConfirm("bulkdel")}><I name="trash" size={14} />Delete</button>
                {bulkErr && <span style={{ fontSize: "var(--text-sm)", color: "var(--danger)" }}>{bulkErr}</span>}
              </>
            )}
          </div>
        )}

        {/* The count is what is actually on screen, not what the signal counted:
            a flagged plan the list does not hold cannot be shown here. */}
        {flagged && (
          <div className="bi-bulk-bar">
            <span className="bi-bulk-bar__count">Showing {rows.length} of {flagged.count} flagged plan{flagged.count === 1 ? "" : "s"}</span>
            <span className="bi-bulk-bar__sep" />
            <span style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>{flagged.title}</span>
            <button className="bi-btn bi-btn--sm" onClick={() => setFlagged(null)}><I name="x" size={14} />Show all plans</button>
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="research-cost-note" style={{ marginTop: 12 }}>{plans.length === 0 ? "No plans yet. Create one, or propose some from what is free in your banks." : "No plans in this stage."}</div>
        )}

        <div ref={listRef} className={viewMode === "list" ? "bi-list--rows" : "bi-list"}>
          {rows.map(p => { const node = viewMode === "list" ? (
            <div className="bi-row" key={p.id} onClick={() => setDetail({ id: p.id })}>
              <div className="bi-row__main"><span className="bi-row__name">{p.title}</span><span className="bi-row__url">{progressText(p.items)}{p.items.drafted ? ` · ${p.items.drafted} with a draft` : ""}</span></div>
              {/* The Section column, which a section filter removes and nothing
                  else, with the row's verb beside it (1.2). */}
              {atAll && (
                <div className="bi-row__badges">
                  <span className="bm-kw-chip">{p.sectionName || "not named"}</span>
                  {!p.canPublish && <span className="bm-kw-chip" title={p.verbWhy || ""}>brief to hand over</span>}
                </div>
              )}
              <div className="bi-row__badges"><span className={"priority-badge priority-" + p.prio}>{p.prio}</span></div>
              <div className="bi-row__meta" style={{ flexBasis: 150, flexGrow: 0 }} onClick={e => e.stopPropagation()}><FD label="Plan status" value={p.stage} opts={STAGES} colorOf={v => STAGE_COLOR[v]} onChange={val => setPlanStage(p.id, val)} /></div>
              <div className="bi-row__meta" style={{ flexBasis: 70, flexGrow: 0 }}>{p.due}</div>
              <div className="bi-row__actions" onClick={e => e.stopPropagation()}>
                <button className="bi-btn bi-btn--sm" onClick={() => setDetail({ id: p.id })}><I name="layers" size={14} />Pieces{p.items.known ? ` (${p.items.pieces})` : ""}</button>
                <button className="bi-iconbtn" title="Edit" onClick={() => setEditing(p)}><I name="edit" size={16} /></button>
                <button className="bi-iconbtn" title="Delete" onClick={() => setConfirm({ kind: "plan", plan: p })}><I name="trash" size={16} /></button>
              </div>
            </div>
          ) : (
            <div className="bi-card" key={p.id} onClick={() => setDetail({ id: p.id })}>
              <div className="bi-card-head">
                <div className="bi-card__name" style={{ fontSize: "var(--text-md)" }}>{p.title}</div>
                <div className="bi-card-badges">
                  {atAll && <span className="bm-kw-chip">{p.sectionName || "not named"}</span>}
                  {atAll && !p.canPublish && <span className="bm-kw-chip" title={p.verbWhy || ""}>brief to hand over</span>}
                  <span className={"priority-badge priority-" + p.prio}>{p.prio}</span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
                <FD label="Plan status" value={p.stage} opts={STAGES} colorOf={v => STAGE_COLOR[v]} onChange={val => setPlanStage(p.id, val)} />
                <span className="research-when"><I name="file-text" size={13} />{progressText(p.items)}</span>
                <span className="research-when"><I name="calendar" size={13} />{p.due}</span>
              </div>
              {/* Progress is the roll-up of the pieces, which is the only place
                  it is true: a plan-level stage would report one thing where the
                  truth is four of seven. */}
              {p.items.known && p.items.pieces > 0 && (
                <div className="ml-progress-row">
                  <div className="ml-progress"><div className="ml-progress__bar" style={{ width: Math.round((p.items.published / p.items.pieces) * 100) + "%" }} /></div>
                  <span className="ml-progress-row__pct">{Math.round((p.items.published / p.items.pieces) * 100)}%</span>
                </div>
              )}
              <div className="bi-card__meta">
                <div className="bi-meta-row"><span className="bi-meta-row__l">Pieces</span><span className="bi-meta-row__v">{p.items.known ? `${p.items.pieces} · ${p.items.drafted} with a draft` : "not reported"}</span></div>
                <div className="bi-meta-row"><span className="bi-meta-row__l">Categories · tags</span><span className="bi-meta-row__v">{p.cats} · {p.tags}</span></div>
                <div className="bi-meta-row"><span className="bi-meta-row__l">Audience · tone</span><span className="bi-meta-row__v">{p.audience} · {p.tone}</span></div>
              </div>
              <p className="research-row-desc">{p.brief}</p>
              <div className="bi-card-foot" onClick={e => e.stopPropagation()}>
                <div className="bi-card-foot__left">
                  <button className="bi-btn bi-btn--sm bi-btn--primary" onClick={() => setDetail({ id: p.id })}><I name="layers" size={14} />Open pieces</button>
                </div>
                <div className="bi-card-foot__right">
                  <button className="bi-iconbtn" title="Edit" onClick={() => setEditing(p)}><I name="edit" size={16} /></button>
                  <button className="bi-iconbtn" title="Delete" onClick={() => setConfirm({ kind: "plan", plan: p })}><I name="trash" size={16} /></button>
                </div>
              </div>
            </div>
          );
          if (!selectMode) return node;
          return (
            <div className={"bi-select-wrap bi-select-wrap--on" + (sel.includes(p.id) ? " bi-select-wrap--selected" : "")} key={p.id}>
              <label className="hds-check bi-select-wrap__check" onClick={ev => ev.stopPropagation()}><input type="checkbox" checked={sel.includes(p.id)} onChange={() => toggleSel(p.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>
              {node}
            </div>
          );
          })}
        </div>

        {editing && window.MlBiModals && <EditPlanModal plan={editing} siteId={connOf(editing)} publish={publish} onClose={() => setEditing(null)} onSaved={reload} />}
        {confirm === "bulkdel" && window.MlBiModals && <window.MlBiModals.ConfirmDialog danger title={`Delete ${sel.length} plan${sel.length === 1 ? "" : "s"}?`} body="The plans and all of their pieces are removed here, and their ingredients go back to the banks. Anything already published in WordPress stays untouched." confirmLabel="Delete" onClose={() => setConfirm(null)} onConfirm={bulkDelete} />}
        {confirm && confirm.kind === "plan" && window.MlBiModals && <window.MlBiModals.ConfirmDialog danger title={`Delete "${confirm.plan.title}"?`} body="The plan and all of its pieces are removed here, and their ingredients go back to the banks. Anything already published in WordPress stays untouched." confirmLabel="Delete" onClose={() => setConfirm(null)} onConfirm={() => { const id = confirm.plan.id; setConfirm(null); delPlan(id); }} />}
      </div>
    );
  }

  window.MlResearchPlanner = { ContentPlanner };
})();
